import type { IdentityAskState } from '../sdk/identity-ask';
/**
 * WebsiteAgentView — the website-agent chat surface (FLO-320 / FLO-349).
 *
 * A top-of-funnel Q&A widget that runs on the SAME merged pipecat session as the
 * demo agent. It renders a bottom-RIGHT launcher (FLO-520) — an Anam-style
 * portrait video tile when the enabled avatar has configured media, else a
 * compact contextual assistant pill. Clicking either immediately warms the shared
 * pipecat session with the microphone disabled; browser microphone permission
 * remains behind the visitor's explicit mic click.
 *
 * The open Q&A grows from the configured launcher corner (full-screen on a
 * phone), or uses a full-height right rail in `displayMode: 'sidebar'`
 * (FLO-519). A live demo opens on the free side of that stable chat;
 * maximizing grows only the video layer beneath it.
 * While collapsed, section-aware nudges sit beside a pill launcher; the
 * bottom-center row is reserved for the live-video tile variant.
 *
 * Self-contained: a dedicated branch off AgentUI, so the demo and onboarding
 * paths are byte-for-byte unchanged when websiteAgent is off.
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { WebsiteAgentChat } from './WebsiteAgentChat';
import { AvatarVideo, corsCacheKeyUrl } from './AvatarVideo';
import { useAvatarRestSequence, usePresenterFlip } from './useAvatarIntroSequence';
import {
  avatarIntroPreloadPlan,
  isMediaReady,
  startAvatarIntroPreload,
} from '../sdk/avatar-intro-preload';
import { connectionIsConstrained } from '../sdk/startup-prefetch';
import { brandImageSources, LauncherMark } from './LauncherMark';
import { LanguageSelector } from './LanguageSelector';
import {
  AgentState,
  ConnectionProgress,
  TranscriptMessage,
  type WebsiteAgentStartOptions,
} from '../types/agent';
import { getString } from '../sdk/ui-strings';
import type { SupportedLanguage } from '../sdk/ui-strings';
import {
  resolveUntaggedOperatorCopy,
  type LanguageOption,
  type PendingLanguageSwitch,
} from '../sdk/language-preference';
import { applySqueeze, SQUEEZE_DURATION_MS } from '../sdk/host-squeeze';
import { hasCalendar, resolveCalendarEmbed } from '../sdk/calendar-embed';
import { BookingSurface } from './BookingSurface';
import {
  isMatchingAttributedBookingUrl,
  isSameBookingRoute,
  resolveBookingIntentDestination,
  type BookingIntentResolver,
} from '../sdk/booking-attribution';
import type { BookingUxHint } from '../sdk/booking-ux-hint';
import {
  HOST_DEMO_CTA_EVENT,
  hostDemoCtaAction,
} from '../sdk/host-cta';
import {
  DEMO_HANDOVER_CAP_MS,
  DEMO_HANDOVER_GIVE_UP_MS,
  DEMO_HANDOVER_FADE_MS,
  demoHandoverCardVisible,
  demoHandoverHoldsStage,
  IDLE_DEMO_HANDOVER,
  reduceDemoHandover,
} from '../sdk/demo-handover';
import {
  HOST_EXIT_INTENT_EVENT,
  exitIntentMessageFromEvent,
} from '../sdk/exit-intent';
import {
  clearConversationContinuity,
  lazyStartAction,
  persistTranscriptPayload,
  readOpenState,
  readTranscript,
  serializeTranscript,
  shouldRestoreOpen,
  writeOpenState,
  type PersistedMessage,
} from '../sdk/wa-continuity';
import { showsFreshStartNotice, type ResumeOutcome } from '../sdk/wa-resume';
import { matchesViewportTier, useChipRowCapacity, useViewportTier } from '../hooks/useViewportTier';
import { useIsPortrait } from '../hooks/useIsPortrait';
import { useIsNarrowPhone } from '../hooks/useIsNarrowPhone';
import { useIsShortViewport } from '../hooks/useIsShortViewport';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import {
  AVATAR_EDGE_BLUR,
  avatarEdgeFade,
  anchoredChipRowLayout,
  avatarHeroSource,
  chipRowLayout,
  clampSidebarWidth,
  compactAvatarPresenterSize,
  cornerPresenterBox,
  demoStageHeaderRightPadding,
  demoStageLayout,
  demoStageStacked,
  demoStageVideoBoxStyle,
  demoStageVideoStyle,
  floatingPanelLayout,
  launcherAnchor,
  launcherNudgeSurfaceHeight,
  launcherTileSize,
  launcherVariant,
  railLayout,
  resolveAvatarMedia,
  resolveDisplayMode,
  resolveLauncherInset,
  shouldSqueeze,
  LAUNCHER_MAX_WIDTH_PX,
  LAUNCHER_NUDGE_SCROLLSPY_DWELL_MS,
  LAUNCHER_NUDGE_REVEAL_MS,
  LAUNCHER_PILL_STACK_OFFSET_PX,
  LAUNCHER_SCROLL_IDLE_MS,
  LAUNCHER_TILE_CTA_BOTTOM_COMPACT_PX,
  LAUNCHER_TILE_CTA_BOTTOM_PX,
  LAUNCHER_TILE_KEYBOARD_BOTTOM_COMPACT_PX,
  LAUNCHER_TILE_KEYBOARD_BOTTOM_TOUCH_PX,
  LAUNCHER_TILE_KEYBOARD_SIZE_PX,
  LAUNCHER_VIDEO_TIMEOUT_MS,
  LAUNCHER_TILE_Z,
  PRESENTER_HANDOFF_MS,
  APPROACH_MAX_MS,
  PILL_KEYBOARD_RIGHT_PX,
  PILL_KEYBOARD_SIZE_PX,
  PILL_KEYBOARD_TOUCH_GUTTER_PX,
  POST_CALL_BOOKING_OFFER_MS,
  avatarDockWidth,
  FLOATING_PANEL_WIDTH_PX,
  RAIL_AVATAR_HEIGHT_PX,
  RAIL_TRANSITION,
  type AvatarMedia,
  type AvatarIntroMedia,
  type PresenterBox,
  type LauncherPosition,
  type LauncherVariant,
  type ViewportTier,
  type WebsiteAgentDisplayMode,
} from './websiteAgentLayout';
import { usePresenterAnchor } from './use-presenter-anchor';
import type { PresenterAnchorState } from './use-presenter-anchor';

/** A section/scroll nudge (mirrors floe-api websiteAgent.nudges.contentMap). */
export interface WebsiteAgentNudge {
  id: string;
  /**
   * The page path this nudge belongs to (e.g. "/", "/pricing"). The nudge only
   * fires while the visitor is on this path. Absent/empty = global (legacy
   * configs where `match` alone is a URL substring / selector).
   */
  page?: string;
  /**
   * The section anchor WITHIN `page`: a CSS selector / element id, or a
   * `heading::<text>` token. Empty = page-level (fires anywhere on `page`).
   */
  match: string;
  topic?: string;
  /** The pill copy (a contextual suggested prompt). */
  copy: string;
  /** Per-page operator switch — false hides this page's nudge. Default on. */
  enabled?: boolean;
}

/** Identity-capture config (mirrors floe-api websiteAgent.identityCapture). */
export interface WebsiteAgentCaptureConfig {
  enabled?: boolean;
  /** When to surface the ask. `never`/absent → no ask. */
  timing?: 'never' | 'on_open' | 'after_n_questions' | 'on_demo_intent';
  afterNQuestions?: number;
  fields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      askFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      requiredFields?: Array<'email' | 'name' | 'company' | 'role' | 'phone'>;
      phoneRegion?: string;
}

interface WebsiteAgentViewProps {
  connected: boolean;
  agentState: AgentState;
  /** Mic (voice input) state — true once the visitor opts into talking. */
  voiceEnabled: boolean;
  botTranscript: string;
  userTranscript: string;
  transcriptHistory: TranscriptMessage[];
  userIsSpeaking: boolean;
  botIsSpeaking: boolean;
  audioLevels: number[];
  connectionProgress?: ConnectionProgress;
  fatalError?: string;
  /** Mic mute/unmute — the "Speak with {name}" voice opt-in. */
  onToggleMute?: () => void;
  /** Browser permission was explicitly denied. */
  micPermissionDenied?: boolean;
  /** The requested microphone is missing, busy, or otherwise unavailable. */
  micUnavailable?: boolean;
  identityAsk?: IdentityAskState | null;
  onIdentityConfirm?: (value: string) => boolean | void;
  onIdentityReject?: () => void;
  onRequestIdentityAskNow?: (timing: string) => void;
  onSendText?: (text: string) => void;
  /** Speaker (bot audio) mute — silences the bot's voice while the session stays
   *  live. Defaults to UNMUTED: the agent speaks its replies from turn 1, and
   *  muting here also stops server-side TTS generation (FLO-348). */
  speakerMuted?: boolean;
  onToggleSpeaker?: () => void;
  /** End the session entirely (tears down pipecat), then return to the closed
   *  acquisition launcher. */
  onEndSession?: () => void;
  /** FLO-323: the live-demo screencast (bot video track). When present, an inline
   *  demo panel opens beside the chat. Null during the Q&A phase. */
  botVideoStream?: MediaStream | null;
  /** Boots the Q&A session. Device access is requested only when the explicit
   *  voice action supplies `requestMicrophone`; text-first starts stay mic-free. */
  onStartWebsiteAgent: (options?: WebsiteAgentStartOptions) => void | Promise<void>;
  /**
   * FLO-786: request the microphone once the agent's spoken welcome ends.
   * Forwarded to the chat ONLY for a session this view started from a voice
   * launcher — see `voiceEntry`. Resolves false when the browser reached no
   * decision, which is the chat's cue to fall back to its tap invitation.
   */
  onRequestMicAfterWelcome?: () => Promise<boolean>;
  /** Records a manual launcher open as engagement so exit intent cannot re-open
   *  or rewrite the panel while its warm session is starting. */
  onOpenWebsiteAgentPreview?: () => void;
  /** Ask the server to start the live demo without adding a visitor message. */
  onRequestLiveDemo?: () => void;
  /** Ask the server to voice the identity card's short, one-shot prompt. */
  onRequestDemoIdentityPrompt?: () => void;
  /** Tell the server the one-shot identity card was submitted or dismissed. */
  siteName?: string;
  siteDomain?: string;
  siteLogoUrl?: string;
  /** Self-hosted favicon, used when the primary logo is absent or fails. */
  siteFaviconUrl?: string;
  /** Brand accent (#RRGGBB) for the launcher letter-mark. */
  accentColor?: string;
  /** Shared closed-launcher placement, configured at Floe() init. */
  launcherPosition?: LauncherPosition;
  launcherInset?: number;
  // ─── websiteAgent config (resolved server-side, FLO-318) ───────────────────
  /** Opener suggestion chips (FLO-307). */
  openerChips?: string[];
  /** FLO-361: server-pushed, conversation-aware follow-up suggestions (the
   *  `suggested_options` channel). Rendered as the follow-up chips after each
   *  answer, in place of the old static opener leftovers. */
  suggestedOptions?: string[];
  /** Section/scroll nudges → Docket-style floating pills (FLO-328). */
  nudges?: WebsiteAgentNudge[];
  /** FLO-322: a demo is available → surface the "see it live" CTA. */
  demoAvailable?: boolean;
  /**
   * Is this agent entitled to start a session at all? Distinct from
   * `demoAvailable`, which is a DEMO-agent fact — a site can pass that and
   * still have `/start-session` reject the q_and_a path with Forbidden. The
   * host CTA needs both. Absent => older floe-api, so default true.
   */
  websiteAgentEnabled?: boolean;
  /** Optional CTA label override (pushToDemo.ctaLabel). */
  ctaLabel?: string;
  /**
   * FLO-618: the site's Cal/Calendly booking URL, resolved + prospect-prefilled
   * by the SDK (`getResolvedCalendarUrl` — the same value DemoView's post-demo
   * embed reads, so the two agents can never offer different calendars).
   * Undefined/empty means the site never configured one, and every booking
   * affordance on the surface stays unrendered.
   */
  calendarLink?: string;
  /** Records a visitor-initiated booking action. Bot-driven calendar opens do
   *  not call this because they are a response to an earlier chat turn, not a
   *  click on a booking CTA. */
  onBookingCtaClick?: BookingIntentResolver;
  /** Resolve bot-driven calendar intent without recording a synthetic click. */
  onResolveBookingIntent?: BookingIntentResolver;
  /** Provider iframe hint for immediate UX only; webhooks own conversion. */
  onBookingUxHint?: (hint: BookingUxHint) => void;
  /**
   * FLO-618: the bot asked to show the calendar (`show_calendar`, pushed when
   * the visitor says something like "can I talk to someone" / "let's book a
   * call"). A monotonically increasing COUNTER rather than a boolean: a visitor
   * who closes the calendar and asks again must get it back, and a boolean that
   * is already `true` produces no state change to react to. 0/undefined = never
   * asked.
   */
  calendarRequestId?: number;
  /**
   * The website→demo hand-over (sdk/demo-handover.ts). Monotonic counters, for
   * the same reason `calendarRequestId` is one: a visitor can be handed to a
   * demo twice in one page session, and a boolean already `true` is not a state
   * change to react to.
   *
   * `demoStartingId` is the START edge — the stage mounts and the branded card
   * goes up BEFORE the screencast exists. `demoVisibleId` is one of three END
   * edges (with the agent's first word and a safety cap). 0/0 means an older
   * pipecat that sends neither, and the surface then behaves exactly as it did
   * before: the stage mounts when the video track arrives, with no card.
   */
  demoStartingId?: number;
  demoVisibleId?: number;
  /** FLO-338: optional per-site talking-head avatar as the "Speak with" face. */
  avatarEnabled?: boolean;
  /** The live avatar track, when present (else a static branded face). */
  avatarVideoStream?: MediaStream | null;
  /**
   * FLO-520: the site's silent idle loop (`websiteAgent.avatar.idleLoopUrl`).
   * ONE asset, three jobs: it is the closed-state launcher tile, the panel's
   * empty-state avatar hero, and — later — the paused state of a live avatar
   * session. When the avatar is enabled, its presence selects the video-tile
   * launcher; disabled, absent, or an older API → the "Ask {agent}" pill.
   */
  avatarIdleLoopUrl?: string;
  /** FLO-520: still frame for reduced motion / slow network / refused autoplay. */
  avatarPosterUrl?: string;
  /**
   * The raw approach-intro facet (`websiteAgent.avatar.intro`). Validated by
   * `resolveAvatarMedia`; absent or unusable → the launcher rests on the idle
   * loop, as it did before intros existed.
   */
  avatarIntro?: unknown;
  /** FLO-325: config-gated identity capture (anonymous-first). */
  capture?: WebsiteAgentCaptureConfig;
  /** Submit captured identity fields (rides the prospect_prefill path). */
  /** FLO-367: nudge telemetry sink (batched + beacon-flushed by the SDK). */
  onNudgeEvent?: (e: NudgeTelemetryEvent) => void;
  /**
   * The visitor moved to another page without a reload. The SDK may still hold
   * only the nudges of the page it loaded on (website-agent-nudges.ts), so this
   * is its cue to load the site's whole list now.
   */
  onNavigated?: () => void;
  /**
   * CDP Phase 1 (FLO-547): fired on REAL panel open/close transitions only —
   * the FLO-524 restore paints the panel open at mount without firing either.
   * `trigger` is who opened it: the visitor (launcher/expand), a nudge chip,
   * or the SDK itself (a live demo landing forces the panel open).
   */
  onWidgetOpened?: (trigger: 'user' | 'nudge' | 'auto') => void;
  onWidgetClosed?: () => void;
  /**
   * FLO-514/519: how the open chat renders.
   *   'floating' — (default) a corner-anchored Q&A card; full-screen on phones.
   *   'sidebar'  — a full-height right rail; ≥1024px the host page is squeezed
   *                by `sidebarWidth` so nothing hides underneath it, and there is
   *                NO backdrop (the page stays interactive).
   * Resolved defensively — anything but 'sidebar' reads as 'floating'.
   */
  displayMode?: WebsiteAgentDisplayMode;
  /** Rail width in px (sidebar mode). Clamped 320–480; default 400. */
  sidebarWidth?: number;
  /**
   * FLO-544: the turns of a conversation the SERVER resumed (FLO-329), already
   * normalized into the same shape the sessionStorage restore produces. Arrives
   * asynchronously — the SDK opens the conversation while the session boots —
   * and REPLACES the restored history when it does. The SDK only ever sends this
   * when there is something better than what the tab already has, so its
   * presence alone is the instruction to swap.
   */
  resumedMessages?: PersistedMessage[];
  /**
   * Resume honesty (FLO-544): how this session's server-side open/resume
   * ended. 'resumed' = the painted history IS shared memory (keep it,
   * unmarked). 'fresh' — a failed open, a refused thread, or a brand-new one
   * — with restored turns still painted renders the "earlier conversation
   * shown for reference — starting fresh" divider over them, so continuity is
   * never implied falsely. Undefined = no session yet (a dormant restored
   * rail claims nothing about memory).
   */
  resumeOutcome?: ResumeOutcome;
  languageOptions?: LanguageOption[];
  selectedLanguage?: SupportedLanguage;
  pendingLanguageSwitch?: PendingLanguageSwitch | null;
  languageSwitchError?: string | null;
  languageSwitchDisabled?: boolean;
  onLanguageChange?: (language: SupportedLanguage) => void;
}

/** FLO-367: one nudge impression/interaction event. */
export interface NudgeTelemetryEvent {
  type: 'SHOWN' | 'CLICKED' | 'HOVERED' | 'DISMISSED';
  nudgeId: string;
  page: string;
  match?: string;
  topic?: string;
}

// FLO-389: canonical Floe coral — matches DemoView's var(--floe-accent, #FF5C35)
// palette and floe-site's brand CSS, so the chat and demo share one unset-fallback.
const DEFAULT_ACCENT = '#FF5C35';

/** How many section pills to surface at once (current + adjacent sections). */
const MAX_ACTIVE_NUDGES = 3;

/**
 * The visitor's current pathname, kept live across SPA navigation. The SDK
 * overlay mounts once and persists, so a host that uses client-side routing
 * (Next.js/React Router — history.pushState, no `popstate`) would otherwise
 * leave page-scoped nudges pinned to the page the overlay first mounted on.
 * We patch pushState/replaceState (call-through, restored on unmount — the
 * standard analytics-SDK technique) plus listen to popstate for back/forward.
 */
function useCurrentPath(): string {
  const [path, setPath] = useState(() =>
    typeof location !== 'undefined' ? location.pathname : '',
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setPath(location.pathname);
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    const wrappedPush = function (this: History, ...args: Parameters<History['pushState']>) {
      const r = origPush.apply(this, args);
      update();
      return r;
    };
    const wrappedReplace = function (this: History, ...args: Parameters<History['replaceState']>) {
      const r = origReplace.apply(this, args);
      update();
      return r;
    };
    history.pushState = wrappedPush;
    history.replaceState = wrappedReplace;
    window.addEventListener('popstate', update);
    update();
    return () => {
      window.removeEventListener('popstate', update);
      // Only restore if OURS is still the installed wrapper — another patcher
      // (e.g. the SDK's NavigationCompleteDetector) may have wrapped on top of
      // us; blindly restoring would clobber its wrapper. If we're not on top,
      // leave ours in place (it only calls through + a no-op setState).
      if (history.pushState === wrappedPush) history.pushState = origPush;
      if (history.replaceState === wrappedReplace) history.replaceState = origReplace;
    };
  }, []);
  return path;
}

/**
 * Give the host page the first claim on attention without erasing the agent's
 * context. Scrolling scales the whole launcher down (copy included), then a
 * five-second quiet beat restores it. Contextual nudges have one independent
 * reveal delay and remain section-aware while the visitor scrolls.
 */
function useLauncherIdlePresentation(enabled: boolean): {
  nudgesReady: boolean;
  scrolling: boolean;
  launcherExpanded: boolean;
  setLauncherPointerActive: (active: boolean) => void;
  setLauncherKeyboardFocusActive: (active: boolean) => void;
} {
  const [state, setState] = useState({
    nudgesReady: false,
    scrolling: false,
    pointerActive: false,
    keyboardFocusActive: false,
  });

  const setLauncherPointerActive = useCallback((active: boolean) => {
    if (!enabled) return;
    setState((current) =>
      current.pointerActive === active ? current : { ...current, pointerActive: active },
    );
  }, [enabled]);

  const setLauncherKeyboardFocusActive = useCallback((active: boolean) => {
    if (!enabled) return;
    setState((current) =>
      current.keyboardFocusActive === active
        ? current
        : { ...current, keyboardFocusActive: active },
    );
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setState((current) =>
        current.nudgesReady ||
        current.scrolling ||
        current.pointerActive ||
        current.keyboardFocusActive
          ? {
              nudgesReady: false,
              scrolling: false,
              pointerActive: false,
              keyboardFocusActive: false,
            }
          : current,
      );
      return;
    }

    let nudgeTimer: ReturnType<typeof setTimeout> | undefined;
    let scrollIdleTimer: ReturnType<typeof setTimeout> | undefined;
    const clearTimers = () => {
      if (nudgeTimer) clearTimeout(nudgeTimer);
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
    };
    const onScroll = () => {
      // A fixed launcher remains hovered when the page moves beneath it, so
      // clear that stale pointer reveal on EVERY scroll event. Genuine
      // keyboard-visible focus is different: preserve it until blur so PageDown
      // or arrow-key scrolling cannot hide the still-focused keyboard action.
      setState((current) =>
        current.scrolling && !current.pointerActive
          ? current
          : {
              ...current,
              scrolling: true,
              pointerActive: false,
            },
      );
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
      scrollIdleTimer = setTimeout(() => {
        setState((current) => ({ ...current, scrolling: false }));
      }, LAUNCHER_SCROLL_IDLE_MS);
    };

    nudgeTimer = setTimeout(() => {
      setState((current) => ({ ...current, nudgesReady: true }));
    }, LAUNCHER_NUDGE_REVEAL_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimers();
      window.removeEventListener('scroll', onScroll);
    };
  }, [enabled]);

  return {
    nudgesReady: state.nudgesReady,
    scrolling: state.scrolling,
    launcherExpanded: state.pointerActive || state.keyboardFocusActive,
    setLauncherPointerActive,
    setLauncherKeyboardFocusActive,
  };
}

/**
 * Track which nudges apply to the visitor's current page + scroll position.
 * Each nudge is scoped to its `page` path — dormant unless the visitor is on
 * that path (so a `heading::FAQ` on /pricing never fires on /security). On the
 * current page, section nudges resolve to their heading element and a scrollspy
 * picks the one most recently scrolled past the top of the viewport; before the
 * first section — or when a section can't be resolved (DOM drift on the embedding
 * site) — the page's page-level baseline nudge shows instead. Legacy nudges with
 * no `page` keep the old
 * selector / URL-substring behavior. Fully self-contained: reads the host
 * `document` directly (the SDK overlay's Shadow DOM isolates styling, not DOM
 * queries). Returns a window of up to MAX_ACTIVE_NUDGES pills — the current
 * section plus the section(s) just above it — with the current section last.
 */
function useActiveNudges(
  nudges: WebsiteAgentNudge[],
  onNavigated?: () => void,
): WebsiteAgentNudge[] {
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const pathname = useCurrentPath();
  // Report each move to another path, never the path the page loaded on. Held
  // in a ref so a new callback identity per render cannot re-fire the report.
  const onNavigatedRef = useRef(onNavigated);
  useEffect(() => {
    onNavigatedRef.current = onNavigated;
  }, [onNavigated]);
  const reportedPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname === reportedPathRef.current) {
      return;
    }
    reportedPathRef.current = pathname;
    onNavigatedRef.current?.();
  }, [pathname]);
  // Last committed active-set key — lets the effect skip setState when nothing
  // changed, so the (per-render-new) `nudges` array can't drive a re-render loop.
  const lastKeyRef = useRef<string>('');

  useEffect(() => {
    if (typeof document === 'undefined' || nudges.length === 0) {
      setActiveIds([]);
      lastKeyRef.current = '';
      return;
    }

    const EXCLUDE = 'nav, header, footer, [role="navigation"], [role="contentinfo"], [role="banner"]';
    const norm = (s: string | null) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

    // Normalize a path/URL to a comparable pathname (trailing slash stripped, lower).
    const normPath = (p: string): string => {
      if (!p) return '';
      let s = p.trim();
      try {
        s = new URL(s, location.href).pathname;
      } catch {
        /* keep raw */
      }
      if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
      return s.toLowerCase();
    };
    const here = normPath(pathname);

    // Resolve a section `match` to the ELEMENT whose scroll position drives the
    // nudge. For `heading::` we anchor on the HEADING itself — small and unique
    // per nudge — NOT its section container: containers are tall and often shared
    // by several sub-headings (a "How it works" section wrapping 5 steps), which
    // made pills collide in the element→nudge map and never swap. For a CSS
    // selector we use the first match. Returns null if nothing resolves (the
    // page-level baseline covers that case).
    // Heading text with aria-label/title fallback — must mirror the extractor
    // (nudge-section-extractor.ts) so `heading::` tokens resolve to the same set.
    const headingText = (el: Element) =>
      norm(el.textContent) ||
      norm(el.getAttribute('aria-label')) ||
      norm(el.getAttribute('title'));
    const resolveSectionEl = (match: string): Element | null => {
      if (match.startsWith('heading::')) {
        const target = match.slice('heading::'.length).trim();
        if (!target) return null;
        return (
          Array.from(
            document.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]'),
          ).find((h) => !h.closest(EXCLUDE) && headingText(h) === target) || null
        );
      }
      try {
        return document.querySelector(match);
      } catch {
        return null;
      }
    };

    const targets: Array<{ id: string; match: string; el: Element | null }> = []; // current-page section nudges (el re-resolved lazily)
    const pageLevelIds: string[] = []; // current-page nudges with no section anchor
    const urlNudgeIds: string[] = []; // legacy global nudges (no `page`) matched by URL

    nudges.forEach((n) => {
      const page = (n.page || '').trim();
      const match = (n.match || '').trim();

      // FLO-358: page-scoped nudge — only consider it on its own path.
      if (page) {
        if (normPath(page) !== here) return; // not this page → dormant
        if (!match) {
          pageLevelIds.push(n.id); // page-level baseline — always active on this page
          return;
        }
        // Push as a CANDIDATE even if the heading isn't in the DOM yet — lazy /
        // scroll-revealed sections resolve later, in pickActive.
        targets.push({ id: n.id, match, el: resolveSectionEl(match) });
        return;
      }

      // Legacy global nudge (no `page`): selector match, else URL substring.
      if (!match) return;
      const el = resolveSectionEl(match);
      if (el) targets.push({ id: n.id, match, el });
      else if (typeof location !== 'undefined' && location.href.includes(match)) {
        urlNudgeIds.push(n.id);
      }
    });

    // Scrollspy WINDOW: show up to MAX_ACTIVE_NUDGES pills at once — the section
    // the visitor is in plus the section(s) just above it (so they overlap during
    // a scroll and a section rich enough to carry several nudges can show them
    // together). Anchoring on each nudge's heading position is deterministic and
    // robust to section size (unlike intersectionRatio on tall containers).
    const pickActive = (): string[] => {
      const line = Math.min(240, window.innerHeight * 0.33);
      const withTop = targets
        .map((t) => {
          // Re-resolve lazily-mounted / replaced section headings each eval, so
          // below-the-fold content that mounts after setup can still anchor.
          if (!t.el || !t.el.isConnected) t.el = resolveSectionEl(t.match);
          return t.el ? { id: t.id, top: t.el.getBoundingClientRect().top } : null;
        })
        .filter((t): t is { id: string; top: number } => t !== null);
      // Sections already scrolled past the line, ordered earliest → current. Same
      // section's nudges share a top, so a stable sort keeps them grouped.
      const passed = withTop.filter((t) => t.top <= line).sort((a, b) => a.top - b.top);
      const upcoming = withTop
        .filter((t) => t.top > line && t.top < window.innerHeight)
        .sort((a, b) => a.top - b.top);

      let ids: string[] = [];
      if (passed.length > 0) {
        // The last N = [.. previous, current] — current ends up last, i.e. nearest
        // the launcher at the bottom of the stack.
        ids = passed.slice(-MAX_ACTIVE_NUDGES).map((t) => t.id);
      } else if (upcoming.length > 0) {
        ids = upcoming.slice(0, MAX_ACTIVE_NUDGES).map((t) => t.id); // top of page → the first sections
      } else if (pageLevelIds.length > 0) {
        // FLO-414: no resolvable section → fan up to MAX_ACTIVE_NUDGES page-level
        // nudges (not just the first) so a page whose sections don't extract — the
        // homepage in particular, which gets a guaranteed page-level floor — still
        // shows several pills instead of a lone baseline.
        ids = pageLevelIds.slice(0, MAX_ACTIVE_NUDGES);
      }
      // Legacy URL-substring globals fill any remaining slots.
      for (const u of urlNudgeIds) {
        if (ids.length >= MAX_ACTIVE_NUDGES) break;
        if (!ids.includes(u)) ids.push(u);
      }
      return Array.from(new Set(ids)).slice(0, MAX_ACTIVE_NUDGES);
    };

    // Dwell before swapping the pill: while the visitor is scrolling, the active
    // section changes every few hundred px — swapping the pill each time reads as
    // flicker ("annoying"). Only commit a new section once it's stayed active for
    // LAUNCHER_NUDGE_SCROLLSPY_DWELL_MS, re-checking at fire time, so a quick
    // scroll-through never flashes an intermediate pill. (Docket debounces its
    // callout swaps for the same reason.) The initial pill still shows immediately.
    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    let pendingKey = lastKeyRef.current;

    const apply = (ids: string[], key: string) => {
      lastKeyRef.current = key;
      pendingKey = key;
      setActiveIds(ids);
    };
    const commit = (immediate = false) => {
      const ids = pickActive();
      const key = ids.join('|');
      if (key === lastKeyRef.current) {
        // Back on (or still on) the shown nudge — cancel any pending swap.
        if (swapTimer) {
          clearTimeout(swapTimer);
          swapTimer = undefined;
        }
        pendingKey = key;
        return;
      }
      if (immediate) {
        if (swapTimer) clearTimeout(swapTimer);
        swapTimer = undefined;
        apply(ids, key);
        return;
      }
      if (key === pendingKey) return; // same target already pending — let its timer run
      pendingKey = key;
      if (swapTimer) clearTimeout(swapTimer);
      swapTimer = setTimeout(() => {
        swapTimer = undefined;
        const nowIds = pickActive();
        const nowKey = nowIds.join('|');
        if (nowKey !== lastKeyRef.current) apply(nowIds, nowKey);
        // Resync `pendingKey` to what's actually shown now (whether or not we
        // committed). Without this, a target that reverted before the timer fired
        // (e.g. a lazy layout-shift, which emits no scroll event) would leave
        // pendingKey stale, and a later commit for that same section would hit the
        // `key === pendingKey` guard and never reschedule — the pill sticks.
        pendingKey = lastKeyRef.current;
      }, LAUNCHER_NUDGE_SCROLLSPY_DWELL_MS);
    };

    if (targets.length === 0) {
      commit(true);
      return;
    }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        commit();
        ticking = false;
      });
    };
    commit(true); // initial pill shows immediately, no dwell
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (swapTimer) clearTimeout(swapTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [nudges, pathname]);

  // Preserve activeIds ORDER (the window's current-section-last ordering), not
  // the config order a plain filter would give — so the stack renders correctly.
  return useMemo(() => {
    const byId = new Map(nudges.map((n) => [n.id, n]));
    return activeIds
      .map((id) => byId.get(id))
      .filter((n): n is WebsiteAgentNudge => !!n);
  }, [nudges, activeIds]);
}

export const WebsiteAgentView: React.FC<WebsiteAgentViewProps> = ({
  connected,
  agentState,
  voiceEnabled,
  botTranscript,
  userTranscript,
  transcriptHistory,
  botIsSpeaking,
  connectionProgress,
  onToggleMute,
  micPermissionDenied,
  micUnavailable,
  onSendText,
  identityAsk, onIdentityConfirm, onIdentityReject, onRequestIdentityAskNow,
  speakerMuted,
  onToggleSpeaker,
  onEndSession,
  botVideoStream,
  onStartWebsiteAgent,
  onRequestMicAfterWelcome,
  onOpenWebsiteAgentPreview,
  onRequestLiveDemo,
  onRequestDemoIdentityPrompt,
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accentColor,
  launcherPosition,
  launcherInset,
  openerChips,
  suggestedOptions,
  nudges,
  demoAvailable,
  websiteAgentEnabled = true,
  ctaLabel,
  calendarLink,
  onBookingCtaClick,
  onResolveBookingIntent,
  onBookingUxHint,
  calendarRequestId,
  demoStartingId,
  demoVisibleId,
  avatarEnabled,
  avatarVideoStream,
  avatarIdleLoopUrl,
  avatarPosterUrl,
  avatarIntro,
  capture,
  onNudgeEvent,
  onNavigated,
  onWidgetOpened,
  onWidgetClosed,
  displayMode,
  sidebarWidth,
  resumedMessages,
  resumeOutcome,
  languageOptions = [],
  selectedLanguage = 'en',
  pendingLanguageSwitch,
  languageSwitchError,
  languageSwitchDisabled = false,
  onLanguageChange,
}) => {
  // Which surface the open panel is. Resolved defensively (anything but
  // 'sidebar' reads as 'floating'), and FIRST because the continuity restore
  // below is sidebar-only — in floating mode nothing here reads or writes
  // storage at all.
  const mode = resolveDisplayMode(displayMode);
  const sidebarMode = mode === 'sidebar';

  // ─── Continuity across full page loads (FLO-524) ───────────────────────────
  // Read ONCE per page load, in a lazy initializer rather than an effect: on a
  // client-routed host (floe.so) this component survives navigation, and a
  // restore that re-ran per path change would re-open a rail the visitor had
  // just closed. A full load remounts, which is exactly when we want it.
  const [restored] = useState(() =>
    sidebarMode
      ? {
          // The tier is read one-shot here, not from useViewportTier(): this runs
          // before that hook, and the decision is about the viewport the visitor
          // LANDED on. Below the rail tier the sidebar is a full-screen takeover,
          // which must never be what a page load hands a phone.
          open: shouldRestoreOpen(readOpenState(), matchesViewportTier()),
          messages: readTranscript(),
        }
      : { open: false, messages: [] as PersistedMessage[] },
  );
  // The turns from before this page load, rendered as history ABOVE the live
  // ones. Seeded from storage, replaced by the server when it resumes a
  // conversation that has any (FLO-544, below). Outbound they are inert — they
  // are never sent anywhere; the bot's context comes from the server's own copy
  // of the thread — and End drops them.
  const [priorMessages, setPriorMessages] = useState<PersistedMessage[]>(restored.messages);

  // ─── Server reconciliation (FLO-544) ───────────────────────────────────────
  // The server's copy of the conversation, when it resumed one, replaces the
  // tab's WHOLESALE — never merged. Both lists carry positional `wa_prior_<i>`
  // ids, so merging would duplicate the overlapping tail AND collide React keys;
  // the server tail is a superset anyway (30 turns vs 25, and it survives a
  // blocked/evicted store). The SDK does the deciding (reconcileRestoredMessages)
  // — including the "resumed but empty" case, where there is no producer writing
  // rows yet and replacing would DELETE the visitor's history — so by the time a
  // list arrives here it is already the better one. Starts no session and opens
  // nothing: this is history for a rail the visitor is already looking at.
  useEffect(() => {
    if (!resumedMessages || resumedMessages.length === 0) return;
    setPriorMessages(resumedMessages);
  }, [resumedMessages]);

  // Launcher (closed) vs chat window (open).
  const [panelOpen, setPanelOpen] = useState(restored.open);
  // CDP Phase 1 (FLO-547): who is about to open the panel. Stamped by the
  // specific handler BEFORE its setPanelOpen(true), read by the transition
  // effect below. A ref, not state — it must never cause a render, and the
  // stamp + flip happen in the same synchronous handler.
  const openTriggerRef = useRef<'user' | 'nudge' | 'auto'>('user');
  // Previous visible-agent state, SEEDED with the restored value: a page load
  // that paints the rail open (the FLO-524 restore) starts with prev===open, so
  // the effect sees no transition and the restore is NOT reported as an open.
  const prevWidgetVisibleRef = useRef(restored.open);
  // Set only by the passive exit-intent event. Null keeps the ordinary website
  // greeting; an object selects the dedicated "before you go" copy without
  // booting a session or claiming the visitor said anything.
  const [exitIntentOffer, setExitIntentOffer] = useState<{ message?: string } | null>(null);
  // Has THIS page load booted a pipecat session? A restored rail and passive
  // exit-intent offer still paint with NO session behind them — auto-starting
  // per pageview is the failure mode continuity must not introduce. An explicit
  // launcher click is different: it warms pipecat immediately, microphone-off,
  // so the visitor's first turn or demo handoff does not wait on a cold boot.
  const [sessionStarted, setSessionStarted] = useState(false);
  // A rejected warm boot must re-arm the UI for retry. Sequence attempts so a
  // late failure from an older start cannot reset a newer live session.
  const startAttemptRef = useRef(0);
  // A click-opened preview should put the caret in the composer. Passive opens
  // (continuity restore, exit intent, host-driven demo) must not steal focus
  // from the host page, so the source of the open is kept separately.
  const [focusComposerOnOpen, setFocusComposerOnOpen] = useState(false);
  // FLO-786: did the VOICE launcher start this session? Only that visitor is
  // asked for a microphone automatically. Set at the one choke point every
  // start goes through, and defaulted to text there, so a start path added
  // later cannot become a voice entry by accident.
  const [voiceEntry, setVoiceEntry] = useState(false);
  // FLO-786: every session BOOT, not just an explicit End.
  //
  // `captureSessionId` marks the End boundary and nothing else, so a session
  // that died some other way — a dropped connection, a rejected start — is
  // followed by a restart carrying the SAME id, and the chat's one-ask-per-
  // session latches never clear: that visitor's next welcome falls back to the
  // tap card instead of asking the browser. It cannot simply share
  // `captureSessionId`, because that id also gates identity capture, which is
  // once per session for its own unrelated reasons.
  const [sessionEpoch, setSessionEpoch] = useState(0);
  // A compact-dock restore is neither voice-first nor keyboard-first. Move
  // focus to the stable header control after either layout tree mounts, without
  // focusing the composer and summoning a mobile soft keyboard.
  const [focusHeaderOnRestore, setFocusHeaderOnRestore] = useState(false);
  // An active call minimizes to its stable controls. End is different: it
  // tears the call down and returns directly to the acquisition launcher.
  const minimizedDockVisible = !panelOpen && sessionStarted;
  // The transparent-avatar dock is an active, visible agent surface too. Moving
  // between it and the full conversation is presentation-only and must not split
  // one engagement into artificial close/open spans.
  const widgetVisible = panelOpen || minimizedDockVisible;
  const acquisitionClosed = !panelOpen && !sessionStarted;
  const launcherPresentation = useLauncherIdlePresentation(acquisitionClosed);
  // Booking is a short-lived post-call outcome, not another chat state. It
  // accompanies the ordinary launcher briefly, then gets out of the way.
  const [postCallBookingVisible, setPostCallBookingVisible] = useState(false);
  const [postCallBookingInteracting, setPostCallBookingInteracting] = useState(false);
  // Identity capture is once per real session, but must survive presentation
  // remounts inside that session. End resets it and bumps the child latch epoch.
  const [captureSessionId, setCaptureSessionId] = useState(0);
  const identityAskedSessionRef = useRef<number | null>(null);

  // ─── The website→demo hand-over (sdk/demo-handover.ts) ─────────────────────
  // The stage used to mount on the screencast's first frame and nothing else,
  // which is why the visitor watched the transition's machinery: an empty box,
  // then the app's parked home page, then a throttled navigation. The server now
  // brackets the hand-over, and between its two edges the stage shows a branded
  // card. Every reveal rule lives in the reducer so it can be pinned without a
  // DOM; this component only translates props and events into its edges.
  const [demoHandover, dispatchHandover] = useReducer(reduceDemoHandover, IDLE_DEMO_HANDOVER);
  // The stage is active while EITHER is true, which is the whole change at the
  // top: the hand-over holds it open before a track exists, and the track keeps
  // holding it for a demo that arrived without a hand-over (a direct-demo entry,
  // or an older pipecat that sends no edges at all).
  const demoActive = !!botVideoStream || demoHandoverHoldsStage(demoHandover);
  const handoverCardVisible = demoHandoverCardVisible(demoHandover);
  // Declared BEFORE the arming effect below, deliberately: if one commit both
  // drops the old stream and carries a new START id, effects run in declaration
  // order and the new hand-over wins rather than being retired on arrival.
  useEffect(() => {
    if (!botVideoStream) dispatchHandover({ type: 'demo_over' });
  }, [botVideoStream]);
  // START, and the authoritative reset: the SDK zeroes both counters on End, on
  // a retry, and on `demo_ended`, so a 0 here means "no transition in flight"
  // rather than "an older server" — both want the same idle state.
  // The stage's <video>, kept so a START can ask it a question an event cannot
  // answer: `playing` fires once per playback, so a hand-over that arms over a
  // picture that is ALREADY running would wait for an event that never comes and
  // hold its card to the cap. Read the element instead.
  const demoVideoElRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!demoStartingId) {
      dispatchHandover({ type: 'demo_over' });
      return;
    }
    dispatchHandover({ type: 'demo_starting', startId: demoStartingId });
    const el = demoVideoElRef.current;
    if (el && !el.paused && el.readyState >= 3) dispatchHandover({ type: 'video_playing' });
  }, [demoStartingId]);
  // END edge (a): the server says the picture is worth looking at.
  useEffect(() => {
    if (demoVisibleId) dispatchHandover({ type: 'demo_visible' });
  }, [demoVisibleId]);
  // END edge (b): the agent starts talking over the picture. A RISING edge only
  // — a hand-over that begins while the agent is still finishing its previous
  // sentence must not reveal on speech that started before the transition did.
  const botSpeechActiveRef = useRef(botIsSpeaking);
  useEffect(() => {
    const started = botIsSpeaking && !botSpeechActiveRef.current;
    botSpeechActiveRef.current = botIsSpeaking;
    if (started) dispatchHandover({ type: 'bot_speaking' });
  }, [botIsSpeaking]);
  // END edge (c): the cap, and behind it the give-up. Both armed only while the
  // card is up, so neither can fire over a revealed demo, and both cleared by
  // the same effect when the card goes. The cap reveals a picture nobody
  // announced; it never reveals an empty stage — a browser still signing in is a
  // hand-over in progress. Only the give-up releases a stage that got no picture.
  useEffect(() => {
    if (!handoverCardVisible) return;
    const cap = setTimeout(() => dispatchHandover({ type: 'safety_cap' }), DEMO_HANDOVER_CAP_MS);
    const giveUp = setTimeout(
      () => dispatchHandover({ type: 'give_up' }),
      DEMO_HANDOVER_GIVE_UP_MS,
    );
    return () => {
      clearTimeout(cap);
      clearTimeout(giveUp);
    };
    // `startId` too: a second hand-over that arms while the first card is still
    // up is a new budget, not the remainder of the old one.
  }, [handoverCardVisible, demoHandover.startId]);
  // Once the visitor has entered the live demo, repeating "See it live" is a
  // stale promise even after the stream ends. Keep that outcome page-scoped so
  // the compact dock and the full conversation agree.
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoMaximized, setDemoMaximized] = useState(false);
  // The chat can move between a sidebar/card and the compact corner. Those
  // parents are intentionally different layout trees, so keep the visitor's
  // unsent text above them rather than relying on child-instance continuity.
  const [chatDraft, setChatDraft] = useState('');
  // Attach via callback ref, not a [botVideoStream] effect. The stage remains
  // mounted while its geometry expands/restores and while chat minimizes; the
  // dependency still covers the stream arriving or changing in place.
  const attachDemoVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      demoVideoElRef.current = el;
      if (el && botVideoStream) {
        el.srcObject = botVideoStream;
        el.play().catch(() => {});
      }
    },
    [botVideoStream],
  );
  // The hand-over's picture gate. Nothing reveals on an END edge alone — both
  // the server's `demo_visible` and the agent's first word routinely land before
  // the first frame, and revealing then would swap a branded card for the empty
  // box it exists to replace.
  const handleDemoVideoPlaying = useCallback(() => {
    dispatchHandover({ type: 'video_playing' });
  }, []);
  // CDP Phase 1 (FLO-547): report REAL agent-surface open/close transitions.
  // Watching the combined full-panel/compact state (instead of instrumenting
  // each handler) guarantees symmetry: the avatar launcher counts immediately,
  // compact ↔ expanded handoffs do not double-count, and End closes the span.
  // The restore-painted initial open remains excluded because the ref is seeded.
  useEffect(() => {
    const prev = prevWidgetVisibleRef.current;
    if (widgetVisible === prev) return;
    prevWidgetVisibleRef.current = widgetVisible;
    if (widgetVisible) onWidgetOpened?.(openTriggerRef.current);
    else onWidgetClosed?.();
    // Consumed — the next open is 'user' unless a handler stamps otherwise.
    openTriggerRef.current = 'user';
  }, [widgetVisible, onWidgetOpened, onWidgetClosed]);

  const startSession = useCallback(
    (
      initialMessage?: string,
      options?: WebsiteAgentStartOptions,
      openPanel = true,
      entry: 'voice' | 'text' = 'text',
    ) => {
      // Kick the boot FIRST — startWebsiteAgentSession() sets
      // websiteAgentSessionStarted=true synchronously (before its first await),
      // so the send below is queued (flushed on onBotReady), not dropped for
      // lack of a client yet.
      const attempt = ++startAttemptRef.current;
      let startResult: void | Promise<void>;
      try {
        startResult = onStartWebsiteAgent(options);
      } catch {
        // Restore the requested presentation even if a custom host callback
        // fails synchronously (panel for text starts, launcher for compact
        // avatar starts).
        setPostCallBookingVisible(false);
        setPostCallBookingInteracting(false);
        setPanelOpen(openPanel);
        setSessionStarted(false);
        setVoiceEntry(false);
        return;
      }
      setPostCallBookingVisible(false);
      setPostCallBookingInteracting(false);
      setPanelOpen(openPanel);
      setSessionStarted(true);
      setVoiceEntry(entry === 'voice');
      setSessionEpoch((epoch) => epoch + 1);
      // FLO-382: send the tapped prompt IMMEDIATELY rather than stashing it until
      // `connected`. sendTextMessage pushes the user bubble synchronously, so the
      // visitor sees their question (and the thinking indicator) at once instead
      // of a multi-second blank gap during WebRTC connect.
      if (initialMessage) onSendText?.(initialMessage);
      if (startResult) {
        void Promise.resolve(startResult).catch(() => {
          if (startAttemptRef.current === attempt) {
            setSessionStarted(false);
            // Matches the synchronous catch above: a start that never happened
            // is not a voice session, and a stale voiceEntry would arm the
            // automatic mic request on whatever starts next.
            setVoiceEntry(false);
          }
          // The SDK owns error reporting and its idle/error render state. This
          // catch only reconciles the view's optimistic start latch.
        });
      }
    },
    [onStartWebsiteAgent, onSendText],
  );

  const handleOpen = useCallback(() => {
    // A deliberate primary-launcher click invites the agent to speak first. It
    // still carries no microphone request of its own: the prompt would land on
    // top of the welcome. FLO-786 asks once that welcome finishes, from the
    // chat's handoff — this 'voice' entry is what arms it.
    onOpenWebsiteAgentPreview?.();
    setExitIntentOffer(null);
    setFocusComposerOnOpen(false);
    if (sessionStarted) setPanelOpen(true);
    else startSession(undefined, { agentFirst: true }, true, 'voice');
  }, [onOpenWebsiteAgentPreview, sessionStarted, startSession]);

  const handleAvatarVoiceOpen = useCallback(() => {
    // The avatar is already the visual orientation, so voice begins in the
    // compact presenter/control surface. Expanding is presentation-only and
    // keeps this same warm session and transcript. Same 'voice' entry as the
    // pill: the mic is asked for when the welcome ends.
    onOpenWebsiteAgentPreview?.();
    setExitIntentOffer(null);
    setFocusComposerOnOpen(false);
    if (sessionStarted) setPanelOpen(false);
    else startSession(undefined, { agentFirst: true }, false, 'voice');
  }, [onOpenWebsiteAgentPreview, sessionStarted, startSession]);

  const handleCollapse = useCallback(() => {
    setFocusComposerOnOpen(false);
    setFocusHeaderOnRestore(false);
    setPanelOpen(false);
    // With no session, collapsing returns to the resting launcher: a calendar
    // left open in the panel (or a lookup still resolving) would otherwise
    // re-home to a stage beside the tile. A live call keeps it — beside an
    // avatar dock, or held for the restored card when there is no avatar.
    if (!sessionStarted) {
      bookingIntentGenerationRef.current += 1;
      setCalendarPending(false);
      setCalendarOpen(false);
    }
  }, [sessionStarted]);
  const handleRestore = useCallback(() => {
    // Restoring an active compact dock is presentation-only: it must not boot a
    // second paid session just because the visitor expanded it.
    setFocusComposerOnOpen(false);
    setFocusHeaderOnRestore(true);
    setPanelOpen(true);
  }, []);
  const handleRestoreFocusApplied = useCallback(() => {
    setFocusHeaderOnRestore(false);
  }, []);

  // ─── Booking (FLO-618) ─────────────────────────────────────────────────────
  // A live-demo session outlives rail, floating and minimized presentations, and an open
  // calendar must survive any chat remount between them.
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [intentCalendarLink, setIntentCalendarLink] = useState<string | null>(null);
  const [calendarPending, setCalendarPending] = useState(false);
  const bookingIntentGenerationRef = useRef(0);
  const currentCalendarLinkRef = useRef(calendarLink);
  currentCalendarLinkRef.current = calendarLink;
  // A configured URL is the gate for every booking affordance.
  const bookable = hasCalendar(calendarLink);


  // A changed calendar resets every booking state that belonged to the old
  // one. The SDK re-rendering with the ATTRIBUTED form of the same route
  // (FLO-729, once a booking intent resolves) is not a changed calendar:
  // resetting on it closed the calendar the visitor had just opened, on the
  // first booking click of every pageview. Only that direction is exempt:
  // the attribution dropping away (consent revoked) still resets, so an open
  // calendar never keeps carrying a Floe reference after it.
  const bookingRouteRef = useRef(calendarLink);
  useEffect(() => {
    const previous = bookingRouteRef.current;
    bookingRouteRef.current = calendarLink;
    if (previous && isMatchingAttributedBookingUrl(previous, calendarLink)) return;
    bookingIntentGenerationRef.current += 1;
    setCalendarPending(false);
    setIntentCalendarLink(null);
    setCalendarOpen(false);
  }, [calendarLink]);
  // Unmount drops any lookup still in flight.
  useEffect(() => () => {
    bookingIntentGenerationRef.current += 1;
  }, []);

  // Keep the booking outcome visible long enough to act on, but never remove a
  // focused/hovered control from underneath the visitor. Leaving it restarts a
  // full offer window; beginning another conversation clears it immediately.
  useEffect(() => {
    if (!postCallBookingVisible || postCallBookingInteracting) return;
    const timer = window.setTimeout(
      () => setPostCallBookingVisible(false),
      POST_CALL_BOOKING_OFFER_MS,
    );
    return () => window.clearTimeout(timer);
  }, [postCallBookingVisible, postCallBookingInteracting]);


  // End is immediate. The call is torn down in one click and the ordinary Ask
  // launcher returns; booking remains as a short-lived companion action only.
  const handleEndSession = useCallback(() => {
    startAttemptRef.current += 1;
    // End also cancels a booking lookup still in flight: a late answer must
    // not open a calendar over the resting launcher, or leave the post-call
    // chip stuck on "Connecting…".
    bookingIntentGenerationRef.current += 1;
    setCalendarPending(false);
    setCalendarOpen(false);
    onEndSession?.();
    setPanelOpen(false);
    // FLO-524: End is the visitor saying "forget this chat" — the one place that
    // intent exists (the SDK's endWebsiteAgentSession is also the cleanup path
    // for a FAILED connect, which must not wipe history still on screen).
    // Dropping the prior turns empties the persisted transcript through the
    // persistence effect; the conversation id goes with it. The restored
    // launcher can then boot a genuinely fresh session.
    setSessionStarted(false);
    setPostCallBookingInteracting(false);
    setPostCallBookingVisible(bookable);
    setFocusComposerOnOpen(false);
    setFocusHeaderOnRestore(false);
    setExitIntentOffer(null);
    setChatDraft('');
    setCaptureSessionId((id) => id + 1);
    setPriorMessages([]);
    if (sidebarMode) clearConversationContinuity();
  }, [onEndSession, sidebarMode, bookable]);


  // ─── Sidebar (docked rail) mode — FLO-514/519 ──────────────────────────────
  // Everything here is inert in the default 'floating' mode: `railMode` is false,
  // so no squeeze or rail focus behavior. (`mode` is resolved at the top of the
  // component — the continuity restore needs it before the first state.)
  const railWidth = clampSidebarWidth(sidebarWidth);
  const tier = useViewportTier();
  // The second axis the tier cannot see: 768px wide is the overlay tier whether
  // it is an upright tablet or a landscape phone, and the demo stage stacks on
  // one and sits beside the dock on the other.
  const portrait = useIsPortrait();
  // The presenter's two axes, neither of which is orientation. Width: the dock
  // is clamped to the viewport, so a 320px phone's is 288px wide and cannot pay
  // for the same presenter a 393px one can. Height: a phone turned sideways is
  // ~400px tall, and the dock is bottom-anchored.
  const narrow = useIsNarrowPhone();
  const short = useIsShortViewport();
  // Read here rather than beside the launcher (its other consumer) because the
  // rail's close window below needs it during render, not just in an effect.
  const reducedMotion = usePrefersReducedMotion();

  // ─── Closed state — launcher variant + chip row (FLO-520) ──────────────────
  // The existing Video avatar switch gates the presenter. Once enabled, its
  // media selects the tile; absent/invalid media falls back to the pill.
  const avatarMedia: AvatarMedia = useMemo(
    () => avatarEnabled
      ? resolveAvatarMedia({ idleLoopUrl: avatarIdleLoopUrl, posterUrl: avatarPosterUrl, intro: avatarIntro })
      : {},
    [avatarEnabled, avatarIdleLoopUrl, avatarPosterUrl, avatarIntro],
  );
  const activeAvatarVideoStream = avatarEnabled ? avatarVideoStream : null;
  // (`reducedMotion` is read up with the rail state — the close window needs it.)
  // A dead/blocked/slow media URL degrades to the pill. Keyed on the URL so a
  // config change (or a fixed CDN) gets a fresh attempt instead of being stuck
  // on the fallback for the rest of the pageview.
  const [failedLoopUrl, setFailedLoopUrl] = useState<string | null>(null);
  const [failedPosterUrl, setFailedPosterUrl] = useState<string | null>(null);
  const handleLoopMediaFailure = useCallback(
    () => setFailedLoopUrl(avatarMedia.idleLoopUrl ?? null),
    [avatarMedia.idleLoopUrl],
  );
  const handlePosterMediaFailure = useCallback(
    () => setFailedPosterUrl(avatarMedia.posterUrl ?? null),
    [avatarMedia.posterUrl],
  );
  // A live stream whose render died. AvatarVideo's layered path keeps the
  // poster up for a dead stream on its own and only reports when the poster
  // has failed too — so by the time this fires there is nothing left in that
  // element, and leaving the stream selected would frame a blank presenter
  // while `hasAvatarWindow` kept the dock (and its panel gates) alive. Keyed
  // on the stream's id so a fresh track from a reconnect gets a fresh attempt.
  const [failedStreamId, setFailedStreamId] = useState<string | null>(null);
  const liveAvatarStream =
    activeAvatarVideoStream && activeAvatarVideoStream.id !== failedStreamId
      ? activeAvatarVideoStream
      : null;
  const handleStreamMediaFailure = useCallback(() => {
    setFailedStreamId(activeAvatarVideoStream?.id ?? null);
    setFailedPosterUrl(avatarMedia.posterUrl ?? null);
  }, [activeAvatarVideoStream, avatarMedia.posterUrl]);
  const loopFailed = !!avatarMedia.idleLoopUrl && failedLoopUrl === avatarMedia.idleLoopUrl;
  const posterFailed = !!avatarMedia.posterUrl && failedPosterUrl === avatarMedia.posterUrl;
  const variant = launcherVariant(avatarMedia, {
    videoFailed: reducedMotion
      ? posterFailed
      : avatarMedia.idleLoopUrl
        ? loopFailed
        : posterFailed,
    reducedMotion,
  });
  // Anchored presenter (docs/designs/website-agent-host-ask.md §D): while the
  // agent is dormant on a desktop with the live-video tile, a host element marked
  // `data-floe-presenter-anchor` can hold the resting presenter. A started
  // session keeps its own dock; everything else keeps the corner.
  const presenterAnchor = usePresenterAnchor(acquisitionClosed && variant === 'tile' && tier === 'squeeze');

  // ─── The approach intro ────────────────────────────────────────────────────
  // With an intro the presenter RESTS a few steps back (far idle loop, the odd
  // gesture), walks up on the click while the live session connects, and walks
  // back when the call ends. Reduced motion gets none of it: the launcher stays
  // the still close poster it has always been for those visitors.
  //
  // A far clip that fails to load retires the intro for the pageview and the
  // launcher falls back to the close loop — NOT to the pill. The close loop is
  // a separate file on a separate request; one bad clip is no evidence against it.
  const [introFailed, setIntroFailed] = useState(false);
  const intro = !reducedMotion && !introFailed && variant === 'tile' ? avatarMedia.intro : undefined;
  const mediaReady = useCallback((url: string) => isMediaReady(corsCacheKeyUrl(url)), []);

  // Preload in priority order: far poster + far idle + APPROACH first (the
  // approach must be buffered before the click), then the close loop, then the
  // gestures and the retreat one at a time in idle time. See the module.
  useEffect(() => {
    if (!intro) return;
    return startAvatarIntroPreload(avatarIntroPreloadPlan(avatarMedia, corsCacheKeyUrl), {
      constrained: connectionIsConstrained(),
    });
  }, [intro, avatarMedia]);

  // 'rest' → 'approach' → 'close'. Derived DURING render from the session edge,
  // not in an effect: an effect would commit one frame of the dock showing the
  // close loop before the approach replaced it, and that frame is a visible
  // jump from the far pose to the close one and back.
  const [introPhase, setIntroPhase] = useState<'rest' | 'approach' | 'close'>('rest');
  const [retreatPending, setRetreatPending] = useState(false);
  const [introSessionSeen, setIntroSessionSeen] = useState(sessionStarted);
  // The element currently holding the presenter — the resting tile's media box
  // or the dock's. The tile and the dock are DIFFERENT boxes (on phones and
  // tablets 107x160 vs 160x240, and 64px apart), so at each session edge the
  // outgoing box is measured here and the incoming surface animates from it
  // (see presenterFlip). Nulls are ignored so the outgoing element is still
  // readable at the edge; a detached one measures 0x0 and yields no animation.
  const presenterElRef = useRef<HTMLElement | null>(null);
  const registerPresenterEl = useCallback((el: HTMLElement | null) => {
    if (el) presenterElRef.current = el;
  }, []);
  const [introFlipFrom, setIntroFlipFrom] = useState<PresenterBox | null>(null);
  if (introSessionSeen !== sessionStarted) {
    setIntroSessionSeen(sessionStarted);
    // Read during render ON PURPOSE: the DOM still holds the previous commit,
    // so this is the outgoing presenter's box. One forced layout per session
    // edge, and only when the avatar has an intro.
    const outgoing = intro ? presenterElRef.current?.getBoundingClientRect() : undefined;
    setIntroFlipFrom(
      outgoing && outgoing.width > 0
        ? { left: outgoing.left, top: outgoing.top, width: outgoing.width, height: outgoing.height }
        : null,
    );
    if (sessionStarted) {
      // Not ready ⇒ skip it. The approach is never waited for: the visitor has
      // clicked, and a presenter frozen at the back of the frame while a clip
      // downloads is worse than the hand-over every site had before intros.
      setIntroPhase(intro && mediaReady(intro.approachUrl) ? 'approach' : 'close');
    } else {
      setRetreatPending(!!intro && introPhase !== 'rest');
      setIntroPhase('rest');
    }
  }
  const endApproach = useCallback(() => setIntroPhase((phase) => (phase === 'approach' ? 'close' : phase)), []);
  // Backstop for an approach that never reports `ended` (autoplay refused, a
  // stalled decode): without it the dock would hold the far pose all call.
  useEffect(() => {
    if (introPhase !== 'approach') return;
    const timer = setTimeout(endApproach, APPROACH_MAX_MS);
    return () => clearTimeout(timer);
  }, [introPhase, endApproach]);
  const avatarApproach = useMemo(
    () =>
      introPhase === 'approach' && intro
        ? { url: intro.approachUrl, posterUrl: intro.farPosterUrl, fromBox: introFlipFrom }
        : null,
    [introPhase, intro, introFlipFrom],
  );
  const clearRetreat = useCallback(() => setRetreatPending(false), []);
  const handleIntroMediaFailure = useCallback(() => setIntroFailed(true), []);

  // What the avatar WINDOW paints (rail top while a demo runs + the full-screen
  // PiP): same source resolution as the chat's empty-state hero, so the face is
  // consistent across every enabled surface. A live stream then wins. A loop
  // that failed anywhere (launcher tile OR window — shared failure state) is
  // excluded, so the source degrades to the poster or the window disappears
  // instead of framing a dead <video>.
  const avatarWindowSource = avatarHeroSource({
    hasStream: !!liveAvatarStream,
    media: {
      idleLoopUrl: loopFailed ? undefined : avatarMedia.idleLoopUrl,
      posterUrl: posterFailed ? undefined : avatarMedia.posterUrl,
    },
    avatarEnabled,
    hasLogo: !!siteLogoUrl,
    reducedMotion,
  });
  // The window only exists when there is a real face to show — a live stream,
  // the idle loop, or its poster. The chat hero's logo/letter fallbacks are for
  // an avatar-less card; as a floating window they would just be a branded
  // placeholder box, so sites without avatar video render no window at all.
  const hasAvatarWindow =
    avatarWindowSource === 'stream' || avatarWindowSource === 'loop' || avatarWindowSource === 'poster';
  // Where booking opens. Beside a presenter the calendar is a STAGE in the
  // live-demo geometry: the live dock during a call, or the resting tile the
  // post-call chip sits beside after one. Opening the panel there would put
  // the face back in a chat card (FLO-754). The pill, an open card and the
  // rail show the calendar as the chat's own view, and so do tablets and
  // phones after a call: beside the 120px tile a stage leaves the calendar a
  // sliver at 768px, and below 768px it would fill the screen underneath a
  // launcher whose every tap starts a paid session.
  const calendarBesidePresenter =
    !panelOpen && (sessionStarted ? hasAvatarWindow : variant === 'tile' && tier === 'squeeze');
  // Read when a booking lookup RESOLVES, up to 4s after the click: by then
  // the visitor may have minimized the card, resized, or lost the tile.
  const calendarBesidePresenterRef = useRef(calendarBesidePresenter);
  calendarBesidePresenterRef.current = calendarBesidePresenter;
  // One shared chroma-key renderer for both homes. Bot audio rides its own
  // track — the avatar media is always hard-muted here.
  const renderAvatarWindow = () => {
    if (avatarWindowSource === 'stream') {
      return (
        <AvatarVideo
          stream={liveAvatarStream!}
          poster={posterFailed ? undefined : avatarMedia.posterUrl}
          reducedMotion={reducedMotion}
          objectFit="contain"
          onError={handleStreamMediaFailure}
        />
      );
    }
    if (avatarWindowSource === 'loop') {
      return <AvatarVideo src={avatarMedia.idleLoopUrl} poster={avatarMedia.posterUrl} reducedMotion={reducedMotion} objectFit="contain" onError={handleLoopMediaFailure} />;
    }
    if (avatarWindowSource === 'poster') {
      return <AvatarVideo poster={avatarMedia.posterUrl} reducedMotion objectFit="contain" onError={handlePosterMediaFailure} />;
    }
    return null;
  };

  const openCalendar = useCallback((trigger: 'user' | 'auto' = 'auto') => {
    setPostCallBookingVisible(false);
    setPostCallBookingInteracting(false);
    setCalendarOpen(true);
    // Beside a presenter (the dock, or the resting tile after a call) the
    // calendar is a stage and the panel stays shut: opening it would evict
    // the face into a chat card, the regression FLO-754 removed for typing.
    // The trigger stamp belongs to the panel-open transition; left behind by
    // a stage, it would be charged to the visitor's next launcher open.
    if (!calendarBesidePresenterRef.current) {
      openTriggerRef.current = trigger;
      setPanelOpen(true);
    }
  }, []);
  const resolveAndOpenCalendar = useCallback(
    async (trigger: 'user' | 'auto') => {
      if (!bookable || !calendarLink) return;
      const requestedCalendarLink = calendarLink;
      const generation = ++bookingIntentGenerationRef.current;
      setCalendarPending(true);
      try {
        const destination = await resolveBookingIntentDestination(
          requestedCalendarLink,
          trigger === 'user' ? onBookingCtaClick : onResolveBookingIntent,
        );
        if (
          generation !== bookingIntentGenerationRef.current ||
          !isSameBookingRoute(currentCalendarLinkRef.current, requestedCalendarLink) ||
          !hasCalendar(destination)
        ) return;
        setIntentCalendarLink(destination);
        openCalendar(trigger);
      } finally {
        if (
          generation === bookingIntentGenerationRef.current &&
          isSameBookingRoute(currentCalendarLinkRef.current, requestedCalendarLink)
        ) {
          setCalendarPending(false);
        }
      }
    },
    [bookable, calendarLink, onBookingCtaClick, onResolveBookingIntent, openCalendar],
  );
  const openCalendarFromUser = useCallback(() => {
    void resolveAndOpenCalendar('user');
  }, [resolveAndOpenCalendar]);
  const closeCalendar = useCallback(() => setCalendarOpen(false), []);
  // A calendar opened beside the resting tile loses its home if the tile
  // degrades to the pill (a dead idle loop) or the viewport drops below the
  // desktop tier (browser zoom, a snapped window). Move it into the panel,
  // where smaller screens already show it: closing it mid-booking would
  // leave the visitor nothing to reopen it with, since the post-call chip
  // is already gone.
  useEffect(() => {
    if (calendarOpen && !panelOpen && !sessionStarted && !calendarBesidePresenter) {
      openTriggerRef.current = 'auto';
      setPanelOpen(true);
    }
  }, [calendarOpen, panelOpen, sessionStarted, calendarBesidePresenter]);

  // The bot's `show_calendar` push (the visitor asked to talk to someone). A
  // counter, not a boolean: closing the calendar and asking again must re-open
  // it. Guarded on `bookable` so a server that pushes for an unconfigured site
  // can't strand the visitor on an empty view, and the 0/undefined first render
  // is skipped so nothing opens on mount.
  const handledCalendarRequestRef = useRef(0);
  useEffect(() => {
    if (
      !calendarRequestId ||
      calendarRequestId <= handledCalendarRequestRef.current ||
      !bookable
    ) return;
    handledCalendarRequestRef.current = calendarRequestId;
    void resolveAndOpenCalendar('auto');
  }, [calendarRequestId, bookable, resolveAndOpenCalendar]);

  // ─── Lazy start (FLO-524) ──────────────────────────────────────────────────
  // A restored rail is a live-looking chat with no session behind it. The first
  // real interaction boots one; the truth table for what else that interaction
  // is allowed to do lives in lazyStartAction(). Launcher previews now share the
  // same dormant posture; once a session exists these are plain pass-throughs.
  const handleSendText = useCallback(
    (text: string) => {
      // The avatar-less compact dock keeps a real composer. Sending from it
      // restores the conversation immediately so the visitor can see their
      // turn and the agent's response without a second click. The AVATAR dock
      // is different: its bubble shows both sides of the exchange, and opening
      // the panel would demote the presenter into a card (FLO-754) — so a
      // typed turn stays exactly where it was typed.
      const keepsDock = hasAvatarWindow && (minimizedDockVisible || !sessionStarted);
      if (!keepsDock) setPanelOpen(true);
      setFocusComposerOnOpen(minimizedDockVisible);
      setFocusHeaderOnRestore(false);
      // startSession() carries the text itself: it flips the SDK's session guard
      // synchronously (before its first await), so the send is QUEUED and
      // flushed at bot-ready instead of dropped for lack of a client.
      if (lazyStartAction('message', sessionStarted).start) startSession(text, undefined, !keepsDock);
      else onSendText?.(text);
    },
    [hasAvatarWindow, minimizedDockVisible, sessionStarted, startSession, onSendText],
  );
  const handleToggleMute = useCallback(() => {
    const { start, forward } = lazyStartAction('mic', sessionStarted);
    if (start) startSession(undefined, { requestMicrophone: true });
    if (forward) onToggleMute?.();
  }, [sessionStarted, startSession, onToggleMute]);
  const sendText = onSendText ? handleSendText : undefined;
  const toggleMute = onToggleMute ? handleToggleMute : undefined;

  // The stage never evicts the chat. A rail remains a rail while the video
  // expands beneath it, which also keeps booking and conversation controls in
  // exactly the same place throughout a demo.
  const railMode = mode === 'sidebar';
  const railOpen = panelOpen && railMode;

  // Squeeze the host page ONLY in the squeeze tier (≥1024px). In the overlay tier
  // the rail sits on top of the page and in fullscreen it takes over, so there is
  // nothing to squeeze. Ref-counted + idempotent in host-squeeze; the cleanup
  // covers close, demo handoff, breakpoint crossing, width change and unmount
  // (the SDK's teardown unmounts the React root, and force-releases as a belt).
  useEffect(() => {
    if (!railOpen || !shouldSqueeze(tier)) return;
    return applySqueeze(railWidth);
  }, [railOpen, tier, railWidth]);

  // ─── Continuity writes (FLO-524) ───────────────────────────────────────────
  // The SINGLE writer of the open flag, which is what makes "close = forget"
  // fall out for free: every close goes through the one setPanelOpen(false) (the
  // header's minimize button and Esc), so the key is removed and the next page
  // does not resurrect a rail the visitor dismissed. Keyed on `mode`,
  // NOT `railMode`, which can change at a responsive breakpoint without the
  // visitor closing the panel.
  useEffect(() => {
    if (!sidebarMode) return;
    writeOpenState(panelOpen);
  }, [sidebarMode, panelOpen]);

  // The visible tail (prior + live), persisted on change.
  //
  // TWO guards, because they stop different costs. The SDK hands a fresh
  // `transcriptHistory` array on EVERY render (`[...this.transcriptHistory]`)
  // and re-renders at the bot-audio-analyser's ~60fps for the whole duration of
  // a spoken reply — so keying the effect on the array identity would run it
  // per animation frame, and serializing first would mean a merge + a per-message
  // rebuild + a `JSON.stringify` of up to 25×4000 chars (~100KB) per frame on
  // the CUSTOMER's main thread. That is the same main-thread pinning that showed
  // up in prod as audio stutter, for a value that changes a few times a minute.
  //   1. `transcriptKey` — O(1), computed in render: turns only ever get
  //      appended (or pruned, which changes the length), and a merge/prune
  //      changes the tail, so length + last id + last content length moves
  //      whenever the payload can. It keeps the effect from RUNNING at all
  //      between turns.
  //   2. the payload comparison — keeps sessionStorage (synchronous, disk-backed)
  //      out of the cases where the key moved but the capped payload did not
  //      (content past MAX_PERSISTED_CONTENT_CHARS, a dropped junk message).
  const tail = transcriptHistory.length > 0 ? transcriptHistory[transcriptHistory.length - 1] : null;
  const transcriptKey = `${transcriptHistory.length}:${tail?.id ?? ''}:${tail?.content.length ?? 0}`;
  const lastPersistedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sidebarMode) return;
    const payload = serializeTranscript([...priorMessages, ...transcriptHistory]);
    if (payload === lastPersistedRef.current) return;
    lastPersistedRef.current = payload;
    persistTranscriptPayload(payload);
    // `transcriptHistory` is read through `transcriptKey`, which is exactly the
    // point — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode, priorMessages, transcriptKey]);

  // Outside the forced-fullscreen tier, Esc collapses the rail (the session
  // stays live — same as the header's minimize). When the video is expanded,
  // the first Esc restores the stage instead and leaves the chat where it is.
  useEffect(() => {
    if (!railOpen || demoMaximized || tier === 'fullscreen') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCollapse();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [railOpen, demoMaximized, tier, handleCollapse]);

  // The expanded demo is still a sibling beneath chat, but outside the forced
  // fullscreen tier it gets Escape-to-restore in both display modes.
  useEffect(() => {
    if (!demoActive || !demoMaximized || tier === 'fullscreen') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDemoMaximized(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [demoActive, demoMaximized, tier]);

  const launcherWrapRef = useRef<HTMLDivElement>(null);
  const compactDockRef = useRef<HTMLElement>(null);
  // An active conversation minimizes straight into its anchored compact dock:
  // the controls stay visible while the host page releases its squeeze, so the
  // rail never slides away and then reappears somewhere else. A rail with no
  // live compact outcome still uses the mirrored 0.3s close window below.
  // Reduced motion skips that window entirely — there is no animation to wait
  // for, only latency.
  //
  // `railClosing` is derived DURING RENDER (React's adjust-state-on-change form),
  // not in an effect: an effect runs a commit too late — the rail has already
  // unmounted — so flipping it there re-mounts a fresh <aside>, which paints one
  // rail-less frame (a blink instead of a slide) and re-runs the chat's mount
  // autofocus, yanking focus off the just-focused launcher into a dismissed
  // surface that is removed 300ms later (focus then lands on <body>).
  // Derived in the same render pass, the <aside> is reconciled in place and the
  // chat instance survives the whole closing window.
  const [railClosing, setRailClosing] = useState(false);
  const [prevRailOpen, setPrevRailOpen] = useState(railOpen);
  if (railOpen !== prevRailOpen) {
    setPrevRailOpen(railOpen);
    // An active session hands off directly to the compact dock. Only a
    // close with no compact outcome enters the slide window; a demo handoff
    // (panelOpen stays true) and reduced motion skip it too.
    setRailClosing(!railOpen && !panelOpen && !minimizedDockVisible && !reducedMotion);
  }
  useEffect(() => {
    if (!railClosing) return;
    const timer = setTimeout(() => setRailClosing(false), SQUEEZE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [railClosing]);
  // Return focus to the launcher when either open surface closes — without this,
  // removing the floating card or `role="complementary"` rail drops focus on
  // <body> and a keyboard user restarts from the top of the host page.
  // Wrapper-scoped (display: contents, no
  // layout effect) so the launcher itself stays untouched. The primary action
  // is selected explicitly because the closed shell also contains a keyboard
  // action, and an active minimized call contains several controls.
  const panelWasOpenRef = useRef(false);
  useEffect(() => {
    if (panelOpen) {
      panelWasOpenRef.current = true;
      return;
    }
    if (railClosing) return;
    if (!panelWasOpenRef.current) return;
    panelWasOpenRef.current = false;
    (compactDockRef.current ?? launcherWrapRef.current)
      ?.querySelector<HTMLElement>('[data-floe-wa-primary-launcher="true"]')
      ?.focus();
  }, [panelOpen, railClosing]);

  // Section-aware nudge chips (shown while collapsed). Operator-disabled
  // pages are filtered out before matching. Memoized so useActiveNudges gets a
  // stable array reference when `nudges` is unchanged (its effect deps on it).
  const enabledNudges = useMemo(
    () =>
      (resolveUntaggedOperatorCopy(nudges, selectedLanguage) || []).filter(
        (n) => n.enabled !== false,
      ),
    [nudges, selectedLanguage],
  );
  const activeNudges = useActiveNudges(enabledNudges, onNavigated);
  // Two visible choices is the conversion ceiling in either layout. Capping
  // here — not in the row component — keeps SHOWN telemetry honest: a prompt
  // that never renders never reports.
  const chipCapacity = useChipRowCapacity();
  const visibleNudges = useMemo(
    // useActiveNudges orders [...previous, current]; keep the tail so the
    // currently viewed section is never the choice discarded by the 2-chip cap.
    () => (chipCapacity > 0 ? activeNudges.slice(-chipCapacity) : []),
    [activeNudges, chipCapacity],
  );
  // The lane these prompts use runs into a calendar standing beside the
  // resting tile, and they would paint over it (LAUNCHER_Z > DEMO_STAGE_Z).
  const closedNudges = useMemo(
    () =>
      acquisitionClosed && !postCallBookingVisible && !calendarOpen && launcherPresentation.nudgesReady
        ? visibleNudges
        : [],
    [
      acquisitionClosed,
      postCallBookingVisible,
      calendarOpen,
      launcherPresentation.nudgesReady,
      visibleNudges,
    ],
  );
  // The closed chips already know which host-page section the visitor is
  // viewing. Carry their full identity into turn zero so the preview stays
  // contextual without losing SHOWN → CLICKED attribution. Configured openers
  // are the fallback only when no contextual prompt is active.
  const previewNudges = useMemo(() => {
    const seenCopy = new Set<string>();
    return activeNudges
      .filter((n) => {
        const copy = n.copy.trim();
        if (!copy || seenCopy.has(copy)) return false;
        seenCopy.add(copy);
        return true;
      })
      .slice(0, 3);
  }, [activeNudges]);
  const previewOpeners = useMemo(() => {
    const configured = (openerChips || []).map((copy) => copy.trim()).filter(Boolean);
    if (previewNudges.length > 0) return previewNudges.map((n) => n.copy.trim());
    if (configured.length > 0) return Array.from(new Set(configured)).slice(0, 4);
    // Do not invent generic sales questions in the renderer. If research and
    // operator config have nothing relevant, offer the one concrete capability
    // the surface can truthfully fulfill — and nothing when it cannot.
    return demoAvailable && !demoStarted
      ? [getString('wa.showLiveDemo')]
      : [];
  }, [previewNudges, openerChips, demoAvailable, demoStarted]);
  // Closed-state copy should answer "what can I ask?" before demanding a click.
  // The current section ends the active window; configured openers are the
  // fallback for pages without an authored contextual nudge.
  const launcherPrompt =
    activeNudges[activeNudges.length - 1]?.copy.trim() || previewOpeners[0]?.trim();

  // FLO-367: emit a SHOWN event the first time each nudge surfaces (deduped for
  // the session), whether it appears in the collapsed row or the open preview.
  const surfacedNudges = useMemo(() => {
    const candidates = panelOpen ? previewNudges : closedNudges;
    return Array.from(new Map(candidates.map((n) => [n.id, n])).values());
  }, [panelOpen, closedNudges, previewNudges]);
  const shownNudgeIdsRef = useRef<Set<string>>(new Set());
  const emitNudgeEvent = useCallback(
    (type: NudgeTelemetryEvent['type'], n: WebsiteAgentNudge) => {
      onNudgeEvent?.({
        type,
        nudgeId: n.id,
        page: n.page || (typeof location !== 'undefined' ? location.pathname : ''),
        match: n.match || undefined,
        topic: n.topic,
      });
    },
    [onNudgeEvent],
  );
  useEffect(() => {
    if (!onNudgeEvent) return;
    for (const n of surfacedNudges) {
      if (shownNudgeIdsRef.current.has(n.id)) continue;
      shownNudgeIdsRef.current.add(n.id);
      emitNudgeEvent('SHOWN', n);
    }
  }, [surfacedNudges, onNudgeEvent, emitNudgeEvent]);

  const handlePreviewOpenerSelection = useCallback(
    (copy: string) => {
      const nudge = previewNudges.find((n) => n.copy.trim() === copy.trim());
      if (nudge) emitNudgeEvent('CLICKED', nudge);
    },
    [previewNudges, emitNudgeEvent],
  );

  const handleClosedNudge = useCallback(
    (nudge: WebsiteAgentNudge) => {
      emitNudgeEvent('CLICKED', nudge);
      onOpenWebsiteAgentPreview?.();
      setExitIntentOffer(null);
      setFocusComposerOnOpen(false);
      // The transition effect consumes this synchronously when the nudge opens
      // the panel, preserving nudge-vs-user attribution in CDP telemetry.
      openTriggerRef.current = 'nudge';
      // A tapped prompt is a typed turn: with a presenter it starts the dock
      // (the answer lands in the lane the chip just left), without one it
      // opens the panel. handleSendText owns that split.
      if (!hasAvatarWindow) setPanelOpen(true);
      // Use the same path as a typed turn. It handles both a cold boot (queueing
      // the text until bot-ready) and an already-live session, so a nudge can
      // never become a telemetry-only click.
      handleSendText(nudge.copy);
    },
    [emitNudgeEvent, onOpenWebsiteAgentPreview, hasAvatarWindow, handleSendText],
  );

  const { currentTranscript, currentRole } = useMemo(() => {
    if (agentState === 'speaking' && botTranscript) {
      return { currentTranscript: botTranscript, currentRole: 'assistant' as const };
    }
    if (agentState === 'listening' && userTranscript) {
      return { currentTranscript: userTranscript, currentRole: 'user' as const };
    }
    return { currentTranscript: '', currentRole: undefined };
  }, [agentState, botTranscript, userTranscript]);

  const accent = accentColor?.trim() || DEFAULT_ACCENT;

  // The hand-over card carries the customer's mark only when there IS one —
  // whatever the launcher already resolved, no new config key. Without an image
  // LauncherMark falls back to a letter on the accent, which is right for a 40px
  // pill and wrong for a 52px centrepiece: one capital letter alone on the stage
  // reads as a missing asset, so the card simply goes without.
  const handoverHasBrandImage = useMemo(
    () => brandImageSources(siteLogoUrl, siteFaviconUrl).length > 0,
    [siteLogoUrl, siteFaviconUrl],
  );
  const handoverMarkLetter = (siteName?.trim()?.[0] || 'F').toUpperCase();

  // A demo CTA is a UI action, not something the visitor said. Start a dormant
  // session first so the SDK can queue the hidden control during boot, then let
  // the server's visible-ready cue own the first orientation.
  const requestDemo = useCallback(() => {
    // FLO-907: `agentFirst` moves the opening to transport-connect instead of
    // the RTVI flush at bot-ready (~1.8s later). A demo CTA is an invitation
    // for the agent to take turn zero exactly like the launcher click is — and
    // the visitor asking for the product has said nothing, so nobody is spoken
    // over. The server no longer depends on this (the `start_demo` control
    // delivers the opening it claims), so a stale CDN bundle is only slower.
    if (lazyStartAction('message', sessionStarted).start) startSession(undefined, { agentFirst: true });
    setDemoStarted(true);
    onRequestLiveDemo?.();
  }, [sessionStarted, startSession, onRequestLiveDemo]);

  const requestDemoIdentityPrompt = useCallback(() => {
    // A compact surface without an avatar restores the full composer. The
    // avatar dock already has a composer beside the presenter.
    if (minimizedDockVisible && !hasAvatarWindow) handleRestore();
    if (lazyStartAction('message', sessionStarted).start) startSession();
    onRequestDemoIdentityPrompt?.();
  }, [minimizedDockVisible, hasAvatarWindow, handleRestore, sessionStarted, startSession, onRequestDemoIdentityPrompt]);

  // Host CTAs can arrive while the chat is unmounted. A monotonic request id
  // survives that open/mount boundary and funnels the click through the exact
  // same identity gate as the in-panel CTA.
  const [externalDemoRequestId, setExternalDemoRequestId] = useState(0);
  const [handledExternalDemoRequestId, setHandledExternalDemoRequestId] = useState(0);
  const markExternalDemoRequestHandled = useCallback((requestId: number) => {
    setHandledExternalDemoRequestId((handled) => Math.max(handled, requestId));
  }, []);

  // The same request, arriving from the HOST PAGE instead of the in-panel chip:
  // floe-site's "See Floe Live" buttons dispatch HOST_DEMO_CTA_EVENT to drive
  // the agent already on the page. Until now the only listener for it lived in
  // DemoView, which never mounts in websiteAgent mode — so every one of those
  // buttons was a silent no-op on floe.so. See sdk/host-cta.ts.
  //
  // It doesn't reuse `requestDemo` because the host CTA starts from states the
  // in-panel chip cannot reach: the panel may be closed and the session may be
  // dormant. hostDemoCtaAction() owns that truth table.
  //
  // Hoisted out of the effect below rather than closed over inside it: a session
  // costs ~$0.33, so `startSession()` must never appear in an effect body — that
  // is the shape an auto-start-per-pageview would take, and wa-continuity.test.ts
  // fails the file for it. The effect only subscribes, leaving the start
  // reachable solely by dispatching the event, i.e. by a visitor's click.
  const surfaceDemoFromHost = useCallback(
    (e: Event) => {
      const { claim, open, start, forward } = hostDemoCtaAction({
        demoAvailable: !!demoAvailable,
        websiteAgentEnabled,
        demoActive,
        sessionStarted,
        ended: false,
      });
      // Unclaimed → the host page falls back to booking a call. Leave the event
      // uncancelled rather than opening a panel that has no demo to give.
      if (!claim) return;
      e.preventDefault();
      // FLO-754: every claimed branch of hostDemoCtaAction returns open:true, so
      // without this gate a host "See it live" click evicted the presenter into
      // the old transcript window — the same eviction the demo effect and the
      // keyboard path already guard against. With a presenter the call lives in
      // the dock and the stage takes the space beside it.
      const opensPanel = open && !hasAvatarWindow;
      // For the branches that start no session (already-live, already-demoing).
      if (opensPanel) setPanelOpen(true);
      // FLO-907: same as `requestDemo` — a host "See Floe Live" click invites
      // the agent to speak first, so the opening starts at transport-connect.
      // startSession() sets the panel itself and opens it by default, so the
      // gate has to be passed in: guarding only the line above left the COLD
      // click (the common one) opening the card over a resting presenter. A
      // dormant card that is already open (exit intent, a restored rail)
      // collapses into the dock too, as it does for a typed turn.
      if (start) {
        // The resets the launcher's own starts make.
        setExitIntentOffer(null);
        setFocusComposerOnOpen(false);
        // Still a TEXT entry, in the dock too: a demo button is not a voice
        // launcher, so the browser is not asked for the mic unrequested
        // (FLO-786, and what security.mdx tells customers). The visitor gets
        // the dock's tap invitation once the welcome ends.
        startSession(undefined, { agentFirst: true }, opensPanel);
      }
      if (start || forward) setExternalDemoRequestId((id) => id + 1);
    },
    [demoAvailable, websiteAgentEnabled, demoActive, sessionStarted, startSession, hasAvatarWindow],
  );

  useEffect(() => {
    window.addEventListener(HOST_DEMO_CTA_EVENT, surfaceDemoFromHost);
    return () => window.removeEventListener(HOST_DEMO_CTA_EVENT, surfaceDemoFromHost);
  }, [surfaceDemoFromHost]);

  // Exit intent has a separate contract from a demo CTA. The latter sends a
  // hidden control and starts a paid session; this one ONLY opens dormant UI.
  // Every path out of dormancy already funnels through lazyStartAction(), so
  // the eventual intent remains the session-cost gate; only voice asks for mic.
  const surfaceExitIntentFromHost = useCallback(
    (e: Event) => {
      // An unentitled or already-engaged agent declines the request. The SDK
      // suppresses its detector when a session starts; these guards keep the
      // component honest if an external script dispatches the event directly.
      // An explicitly opened preview already has the visitor's attention. Do not
      // replace its greeting/prompts with a later mouseleave signal while they
      // are reading or drafting their first question.
      // A calendar open beside the resting tile is engagement too: opening the
      // panel over it would rebuild the calendar inside the card mid-booking.
      if (!websiteAgentEnabled || panelOpen || sessionStarted || demoActive || calendarOpen) return;
      e.preventDefault();
      setExitIntentOffer({ message: exitIntentMessageFromEvent(e) });
      // CDP Phase 1 (FLO-547): the SDK's detector opened this, not a click.
      openTriggerRef.current = 'auto';
      setFocusComposerOnOpen(false);
      setPanelOpen(true);
    },
    [websiteAgentEnabled, panelOpen, sessionStarted, demoActive, calendarOpen],
  );

  useEffect(() => {
    window.addEventListener(HOST_EXIT_INTENT_EVENT, surfaceExitIntentFromHost);
    return () => window.removeEventListener(HOST_EXIT_INTENT_EVENT, surfaceExitIntentFromHost);
  }, [surfaceExitIntentFromHost]);

  // What the chat renders: this page load's turns, with any restored ones in
  // front. One array, so the scroll/typing/capture logic can't tell them apart.
  const conversation = useMemo(
    () => (priorMessages.length > 0 ? [...priorMessages, ...transcriptHistory] : transcriptHistory),
    [priorMessages, transcriptHistory],
  );
  // FLO-524: restored history on screen, no session behind it. Excludes
  // `demoActive` as a belt — a screencast means a live session by definition,
  // and the demo stage is only reachable from one.
  const dormant = panelOpen && !sessionStarted && !demoActive;

  // FLO-325's "once per session" is a SESSION fact, so it is held here rather
  // than inside the chat, where a compact-dock handoff would otherwise reset it
  // and ask again for an email the visitor just provided.


  // FLO-545 review: while a demo runs in RAIL mode the avatar's face is the
  // rail-top window (renderAvatarWindow, pinned above this chat in the same
  // column) — hand the chat a null stream there so its in-conversation strip
  // doesn't paint the same face twice in one rail. The compact branch unmounts
  // that rail-top window, so minimizing hands the stream back to the presenter.
  // Every other chat context keeps the stream as well.
  // FLO-754: both of these read `hasAvatarWindow`, so they live BELOW its
  // derivation rather than above it behind a ref. A ref assigned during render
  // is impure — React may render and discard — and moving only the assignment
  // into a layout effect would be worse: effects fire in declaration order, so
  // an effect declared here would land after the demo effect and hand it the
  // PREVIOUS commit's value on the very render where the demo starts.
  // Opening the panel is right for a chat surface and wrong for a presenter: it
  // would swap the avatar dock for the old transcript window the instant the
  // screencast arrives. With a presenter, the dock stays and the stage takes
  // the space beside it.
  useEffect(() => {
    if (demoActive) {
      if (!hasAvatarWindow) openTriggerRef.current = 'auto';
      setDemoStarted(true);
      if (!hasAvatarWindow) setPanelOpen(true);
    } else setDemoMaximized(false);
  }, [demoActive, hasAvatarWindow]);

  const handleUseKeyboard = useCallback(() => {
    // The keyboard affordance is an explicit text-mode choice: the agent still
    // responds immediately, but its first turn renders silently with mic
    // disabled and bot audio muted. If a warm session already exists, mute it
    // once rather than starting a second connection.
    onOpenWebsiteAgentPreview?.();
    setExitIntentOffer(null);
    setFocusComposerOnOpen(true);
    // FLO-754: with a presenter on the page, typing is a MODE of the dock, not
    // a different surface — opening the panel here would put the avatar back
    // inside a card, which is the thing this redesign exists to stop. The chat
    // reads `focusComposerOnOpen` and starts its bar in the composer.
    const keyboardKeepsDock = hasAvatarWindow;
    if (!sessionStarted) {
      startSession(undefined, { speakerMuted: true, agentFirst: true }, !keyboardKeepsDock);
    } else {
      setPanelOpen(!keyboardKeepsDock);
      if (!speakerMuted) onToggleSpeaker?.();
    }
  }, [onOpenWebsiteAgentPreview, sessionStarted, speakerMuted, onToggleSpeaker, startSession, hasAvatarWindow]);
  // FLO-754: does the minimized surface render the avatar dock? Everything the
  // redesign changes hangs off this one predicate, so a site with the avatar
  // off (or with no renderable media) keeps the previous compact dock exactly.
  useEffect(() => {
    if (identityAsk?.status !== 'proposed') return;
    if (minimizedDockVisible && !hasAvatarWindow) handleRestore();
    setFocusComposerOnOpen(true);
  }, [identityAsk?.askId, identityAsk?.status]);

  const avatarDockActive = minimizedDockVisible && hasAvatarWindow;

  // The booking stage exists only beside a presenter (the live dock or the
  // resting tile); everywhere else the calendar is the chat panel's own view.
  // Resolved here (not in the chat) because the stage is a sibling of the
  // dock and the launcher, not a child of either.
  const stageCalendar = useMemo(
    () => resolveCalendarEmbed(intentCalendarLink ?? calendarLink),
    [intentCalendarLink, calendarLink],
  );
  const bookingStageVisible =
    calendarOpen && calendarBesidePresenter && stageCalendar.fallbackHref !== null;
  // Escape closes the stage and leaves the presenter (dock or resting tile)
  // exactly where it was. A window listener, because the chat's own Escape
  // handler only sees keys typed inside the dock, and after End no chat is
  // mounted at all.
  useEffect(() => {
    if (!bookingStageVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCalendar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [bookingStageVisible, closeCalendar]);

  const chatAvatarStream =
    railMode && demoActive && hasAvatarWindow && !minimizedDockVisible
      ? null
      : liveAvatarStream;

  // The chat surface itself is layout-agnostic (it fills its parent), so both the
  // anchored card and the rail render this exact element — one set of props, no
  // drift between the two modes.
  const chatSurface = (
    <WebsiteAgentChat
      docked={railMode}
      controlsAtBottom
      minimized={minimizedDockVisible}
      onRestore={handleRestore}
      onRestoreDemo={
        demoMaximized && tier !== 'fullscreen' ? () => setDemoMaximized(false) : undefined
      }
      disableEscapeMinimize={tier === 'fullscreen'}
      modal={!railMode && tier === 'fullscreen'}
      connected={connected}
      agentState={agentState}
      messages={conversation}
      currentTranscript={currentTranscript}
      currentRole={currentRole}
      voiceEnabled={voiceEnabled}
      onToggleMute={toggleMute}
      micPermissionDenied={micPermissionDenied}
      micUnavailable={micUnavailable}
      // FLO-786: armed for a voice-launcher start only. A keyboard, nudge, chip
      // or capture start is a text choice and never gets an unrequested prompt.
      // So is a host-page demo CTA, whether it lands in the card or the dock.
      onRequestMicAfterWelcome={voiceEntry ? onRequestMicAfterWelcome : undefined}
      sessionEpoch={sessionEpoch}
      // The composer is always visible and owns the Website Agent voice opt-in.
      // A second copy in the header made one action look like two choices.
      showHeaderMic={false}
      speakerMuted={speakerMuted}
      // Hidden while dormant: startWebsiteAgentSession() resets speakerMuted to
      // false, so a pre-session toggle would silently revert — a lying control,
      // and one that would tempt a visitor into paying for a session with it.
      onToggleSpeaker={dormant ? undefined : onToggleSpeaker}
      onSendText={sendText}
      composerDraft={chatDraft}
      onComposerDraftChange={setChatDraft}
      // FLO-524: history is restored, the session is not (yet).
      dormant={dormant}
      focusComposerOnOpen={focusComposerOnOpen}
      focusHeaderOnRestore={focusHeaderOnRestore}
      onRestoreFocusApplied={handleRestoreFocusApplied}
      exitIntent={exitIntentOffer ?? undefined}
      onMinimize={handleCollapse}
      // A fresh preview has no session to end. Keep one clear close affordance
      // until a conversation exists; restored history still retains End.
      onEndSession={
        onEndSession && (sessionStarted || priorMessages.length > 0)
          ? handleEndSession
          : undefined
      }
      connectionProgress={connectionProgress}
      openerChips={previewOpeners}
      onSelectOpener={handlePreviewOpenerSelection}
      followUpOptions={suggestedOptions}
      demoAvailable={demoAvailable && !demoStarted}
      ctaLabel={ctaLabel}
      onRequestDemo={requestDemo}
      onRequestDemoIdentityPrompt={requestDemoIdentityPrompt}
      externalDemoRequestId={
        externalDemoRequestId > handledExternalDemoRequestId
          ? externalDemoRequestId
          : 0
      }
      onExternalDemoRequestHandled={markExternalDemoRequestHandled}
      captureSessionId={captureSessionId}
      identityAskedSessionRef={identityAskedSessionRef}
      // FLO-618: booking. The link's presence is the only gate — the chat
      // renders no booking control without one.
      calendarLink={intentCalendarLink ?? calendarLink}
      calendarOpen={calendarOpen}
      calendarPending={calendarPending}
      onOpenCalendar={openCalendarFromUser}
      onCloseCalendar={closeCalendar}
      onBookingUxHint={onBookingUxHint}
      // No `bookLabel` here on purpose: the website agent's `ctaLabel` is the
      // pushToDemo label ("See it live"), a DIFFERENT offer — reusing it would
      // put "See it live" on the calendar button. The chat's default stands
      // until booking copy gets its own config field.
      avatarEnabled={avatarEnabled}
      avatarVideoStream={chatAvatarStream}
      onAvatarStreamError={handleStreamMediaFailure}
      compactPresenterSize={compactAvatarPresenterSize(tier, { short, narrow })}
      tier={tier}
      // FLO-520: the launcher tile's idle loop is also the panel's empty-state
      // hero, so the tile visually "becomes" the avatar card when it opens. A
      // media SOURCE, not a hardcoded element — an enabled live avatar stream
      // slots into the same card later.
      avatarMedia={avatarMedia}
      avatarApproach={avatarApproach}
      onAvatarApproachEnded={endApproach}
      registerPresenterEl={registerPresenterEl}
      capture={capture}
      identityAsk={identityAsk} onIdentityConfirm={onIdentityConfirm} onIdentityReject={onIdentityReject} onRequestIdentityAskNow={onRequestIdentityAskNow}
      // FLO-524: the identity trigger counts THIS page load's turns, not the
      // restored ones — see priorMessageCount in WebsiteAgentChat.
      priorMessageCount={priorMessages.length}
      // Resume honesty: the open failed or opened a NEW thread while restored
      // turns are painted — divider them as reference-only so the bot's cold
      // start is never dressed up as memory. Pure gate: wa-resume.test.ts.
      priorStale={showsFreshStartNotice(resumeOutcome ?? null, priorMessages.length)}
      siteName={siteName}
      siteLogoUrl={siteLogoUrl}
      siteFaviconUrl={siteFaviconUrl}
      accent={accent}
      languageOptions={languageOptions}
      selectedLanguage={selectedLanguage}
      pendingLanguageSwitch={pendingLanguageSwitch}
      languageSwitchError={languageSwitchError}
      languageSwitchDisabled={languageSwitchDisabled}
      onLanguageChange={onLanguageChange}
    />
  );

  // The LIVE DEMO surface (header + screencast) has one persistent home. Only
  // its fixed stage geometry changes on expand/restore, so the video element and
  // stream survive while the independent chat opens, minimizes, or restores.
  const demoPane = (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #14141c 0%, #0a0a0f 100%)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '13px 16px',
          paddingRight: demoStageHeaderRightPadding({
            displayMode: mode,
            tier,
            portrait,
            launcherPosition,
            launcherInset,
            sidebarWidth: railWidth,
            expanded: demoMaximized,
            // The maximized stage runs UNDER the dock, so its header has to pad
            // for the same lane the stage itself reserves — one number, read
            // from the tier at every call site.
            reservedChatWidth: avatarDockActive ? avatarDockWidth(tier) : undefined,
          }),
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff5c35', flexShrink: 0, animation: 'floe-demo-pulse 2s ease-in-out infinite' }}
          />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.9)', letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
            {getString('demo.liveDemo')}
          </span>
        </div>
        {tier !== 'fullscreen' && (
          <button
            type="button"
            onClick={() => setDemoMaximized((m) => !m)}
            aria-label={getString(demoMaximized ? 'demo.restore' : 'demo.maximize')}
            className="floe-demo-hdr-btn"
          >
            {demoMaximized ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" /></svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" /></svg>
            )}
          </button>
        )}
      </div>
      <div
        style={demoStageVideoBoxStyle(demoStageStacked({
          displayMode: mode,
          tier,
          portrait,
          launcherPosition,
          launcherInset,
          sidebarWidth: railWidth,
          expanded: demoMaximized,
          reservedChatWidth: avatarDockActive ? avatarDockWidth(tier) : undefined,
        }))}
      >
        <video
          ref={attachDemoVideo}
          className="floe-wa-demo-video"
          onPlaying={handleDemoVideoPlaying}
          autoPlay
          playsInline
          muted
          style={demoStageVideoStyle(tier)}
        />
        {/* The hand-over card. A sibling INSIDE the video box rather than a
            replacement for the video: the element below has to stay mounted so
            the track can attach and start playing behind the card, which is what
            the reveal waits for. The box is the card's frame too — on a phone
            the picture is pinned `center top` (demoStageVideoStyle), so
            centring on the letterbox would drop the card into the slack under
            the strip instead of the middle of what the visitor is looking at.

            Mounted for as long as the hand-over holds the stage rather than for
            as long as the card is VISIBLE: that is what lets the reveal be one
            CSS cross-fade instead of an unmount on a timer the reducer would
            then have to know about. */}
        {demoHandoverHoldsStage(demoHandover) && (
          <div
            className="floe-wa-demo-card"
            data-floe-wa-demo-card={handoverCardVisible ? 'shown' : 'hidden'}
            // Hidden from assistive tech the moment it is revealed, so the line
            // is not still readable behind a picture that has replaced it.
            aria-hidden={handoverCardVisible ? undefined : true}
          >
            {handoverHasBrandImage && (
              <LauncherMark
                siteLogoUrl={siteLogoUrl}
                siteFaviconUrl={siteFaviconUrl}
                letter={handoverMarkLetter}
                accent={accent}
                size={52}
              />
            )}
            {/* Only the WORDS are the live region — the same split the chat's
                status regions use. The mark and the dots are decoration; inside
                the region they would be announced as if they said something. */}
            <span
              className="floe-wa-demo-card-line"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {getString('demo.opening')}
            </span>
            {/* Indeterminate on purpose. A determinate bar would have to claim
                progress it cannot know — a stalled navigation makes every
                percentage a lie — so this only says "something is happening".
                `aria-hidden`: the line above is the announcement. */}
            <span className="floe-wa-demo-card-pulse" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Only the acquisition state owns the animated launcher and nudges. A
          live conversation keeps the same light chat surface mounted below,
          with only its conversation body collapsed. End returns here. */}
      <div ref={launcherWrapRef} style={{ display: 'contents' }}>
        {acquisitionClosed && (
          <>
            <LauncherStyles />
            <WebsiteAgentChipRow
              accent={accent}
              tier={tier}
              variant={variant}
              position={launcherPosition}
              launcherInset={launcherInset}
              siteName={siteName}
              nudges={closedNudges}
              anchor={presenterAnchor}
              onNudge={handleClosedNudge}
              bookingAction={
                postCallBookingVisible
                  ? {
                      label: getString('wa.bookCall'),
                      onClick: openCalendarFromUser,
                      onInteractionChange: setPostCallBookingInteracting,
                      pending: calendarPending,
                    }
                  : undefined
              }
            />
            {onLanguageChange && languageOptions.length > 1 && !presenterAnchor?.box && (
              <div
                style={{
                  ...launcherAnchor(tier, launcherPosition, launcherInset),
                  // The pill stacks its prompts above itself, so the selector
                  // clears them; the tile's prompts live in the lane beside it.
                  bottom:
                    variant === 'pill'
                      ? resolveLauncherInset(tier, launcherInset) +
                        LAUNCHER_PILL_STACK_OFFSET_PX +
                        launcherNudgeSurfaceHeight(
                          variant,
                          closedNudges.length + (postCallBookingVisible ? 1 : 0),
                        ) +
                        10
                      : resolveLauncherInset(tier, launcherInset) +
                        launcherTileSize(tier).height +
                        10,
                }}
              >
                <LanguageSelector
                  languages={languageOptions}
                  value={selectedLanguage}
                  pending={pendingLanguageSwitch}
                  error={languageSwitchError}
                  disabled={languageSwitchDisabled}
                  onChange={onLanguageChange}
                />
              </div>
            )}
            <WebsiteAgentLauncher
              variant={variant}
              anchor={presenterAnchor}
              tier={tier}
              position={launcherPosition}
              launcherInset={launcherInset}
              media={avatarMedia}
              intro={intro}
              playRetreat={retreatPending}
              retreatFromBox={retreatPending ? introFlipFrom : null}
              registerPresenterEl={registerPresenterEl}
              onRetreatConsumed={clearRetreat}
              isMediaReady={mediaReady}
              onIntroMediaFailure={handleIntroMediaFailure}
              reducedMotion={reducedMotion}
              onMediaFailure={
                reducedMotion || !avatarMedia.idleLoopUrl
                  ? handlePosterMediaFailure
                  : handleLoopMediaFailure
              }
              siteName={siteName}
              siteLogoUrl={siteLogoUrl}
              siteFaviconUrl={siteFaviconUrl}
              accent={accent}
              prompt={launcherPrompt}
              scrolling={launcherPresentation.scrolling}
              expanded={launcherPresentation.launcherExpanded}
              onPointerInteractionChange={launcherPresentation.setLauncherPointerActive}
              onKeyboardFocusChange={launcherPresentation.setLauncherKeyboardFocusActive}
              onOpen={variant === 'tile' ? handleAvatarVoiceOpen : handleOpen}
              onUseKeyboard={handleUseKeyboard}
            />
          </>
        )}
      </div>

      {/* Active sessions switch directly into the compact dock. `railClosing`
          only keeps a non-session rail alive past dismissal; the floating card
          must never render from it. */}
      {demoActive && (
        <section
          data-floe-wa-demo-stage="true"
          // The hand-over's one rendered switch: the card's opacity and the
          // screencast's are both CSS off this attribute, so the reveal is a
          // single cross-fade rather than two states that can disagree.
          data-floe-wa-demo-handover={handoverCardVisible ? 'card' : 'off'}
          className="floe-wa-demo-stage"
          role="region"
          aria-label={getString('demo.liveDemo')}
          style={{
            ...demoStageLayout({
              displayMode: mode,
              tier,
              portrait,
              launcherPosition,
              launcherInset,
              sidebarWidth: railWidth,
              expanded: demoMaximized,
              reservedChatWidth: avatarDockActive ? avatarDockWidth(tier) : undefined,
            }),
            // The card's dots are the site's accent; the launcher sets this var
            // on its own shell, and the stage is not inside it.
            ...({ '--floe-wa-accent': accent } as React.CSSProperties),
          }}
        >
          {/* The stage's OWN stylesheet. The block below (keyframes for the live
              dot, the stage's inset transition) is gated on the chat panel being
              mounted, which the stage is not: it can be up before the panel
              opens, so a card whose animation lived there would silently not
              run. Scoped to this Shadow DOM like every other style here. */}
          <style>{`
            .floe-wa-demo-video { transition: opacity ${DEMO_HANDOVER_FADE_MS}ms ease; }
            .floe-wa-demo-stage[data-floe-wa-demo-handover="card"] .floe-wa-demo-video { opacity: 0; }
            .floe-wa-demo-card {
              position: absolute; inset: 0; z-index: 1;
              display: flex; flex-direction: column; align-items: center; justify-content: center;
              gap: 18px; padding: 24px; box-sizing: border-box; text-align: center;
              pointer-events: none;
              background: linear-gradient(180deg, #14141c 0%, #0a0a0f 100%);
              transition: opacity ${DEMO_HANDOVER_FADE_MS}ms ease;
            }
            .floe-wa-demo-card[data-floe-wa-demo-card="hidden"] { opacity: 0; }
            .floe-wa-demo-card-line {
              max-width: 280px;
              font-family: ${LAUNCHER_FONT};
              font-size: 15px; font-weight: 500; line-height: 1.45;
              letter-spacing: -0.01em; color: rgba(255,255,255,0.9);
            }
            .floe-wa-demo-card-pulse { display: flex; align-items: center; gap: 6px; }
            .floe-wa-demo-card-pulse i {
              width: 5px; height: 5px; border-radius: 50%;
              background: var(--floe-wa-accent, ${DEFAULT_ACCENT});
              animation: floe-wa-demo-card-pulse 1.25s ease-in-out infinite;
            }
            .floe-wa-demo-card-pulse i:nth-child(2) { animation-delay: 0.18s; }
            .floe-wa-demo-card-pulse i:nth-child(3) { animation-delay: 0.36s; }
            @keyframes floe-wa-demo-card-pulse {
              0%, 100% { opacity: 0.28; transform: scale(0.85); }
              50% { opacity: 1; transform: scale(1); }
            }
            @media (prefers-reduced-motion: reduce) {
              /* Three steady dots still read as deliberate; a frozen sweep bar
                 would read as a stalled one. Both fades go too — a cross-fade
                 is motion. */
              .floe-wa-demo-card-pulse i { animation: none; opacity: 0.55; transform: none; }
              .floe-wa-demo-card, .floe-wa-demo-video { transition: none; }
            }
          `}</style>
          {demoPane}
        </section>
      )}

      {/* The calendar as a STAGE (FLO-618 × FLO-754): with a presenter on the
          page, booking takes the same framed geometry the live demo uses —
          the dock (or, after a call, the resting tile) keeps its corner and
          its face, the calendar takes the free side. The chat panel's
          in-body calendar view still serves every surface without a
          presenter. */}
      {bookingStageVisible && (
        <section
          data-floe-wa-booking-stage="true"
          className="floe-wa-demo-stage"
          role="region"
          aria-label={getString('wa.bookCall')}
          style={{
            ...demoStageLayout({
              // The stage only shows while the panel is shut, and a shut rail
              // minimizes to the corner dock, so there is never a rail beside
              // it to reserve: always the floating geometry, clear of the dock
              // (or the resting tile) on the launcher's side.
              displayMode: 'floating',
              tier,
              // Never stacked: a calendar is TALL, so the narrow column beside
              // the dock suits it — the shape that strands a 16:9 screencast is
              // the shape a booking widget wants.
              portrait: false,
              launcherPosition,
              launcherInset,
              expanded: false,
              reservedChatWidth: avatarDockWidth(tier),
            }),
            background: '#FFFFFF',
          }}
        >
          <BookingSurface
            calendar={stageCalendar}
            title={getString('wa.bookCall')}
            accent={accent}
            onClose={closeCalendar}
            onBookingUxHint={onBookingUxHint}
            closeKind="close"
          />
        </section>
      )}

      {(panelOpen || minimizedDockVisible || (railClosing && railMode)) && (
        <>
          <style>{`
            @keyframes floe-wa-anchor-pop { from { opacity: 0; transform: translateY(14px) scale(0.96); } to { opacity: 1; transform: none; } }
            @keyframes floe-wa-rail-in  { from { transform: translateX(100%); } to { transform: translateX(0); } }
            @keyframes floe-wa-rail-out { from { transform: translateX(0); } to { transform: translateX(100%); } }
            @keyframes floe-demo-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
            .floe-demo-hdr-btn {
              display: flex; align-items: center; justify-content: center;
              width: 30px; height: 30px; border-radius: 9px; border: none;
              background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.72);
              cursor: pointer; transition: background 0.15s ease, color 0.15s ease;
            }
            .floe-demo-hdr-btn:hover { background: rgba(255,255,255,0.16); color: #fff; }
            .floe-wa-demo-stage {
              transition: inset 0.36s cubic-bezier(0.16,1,0.3,1), border-radius 0.36s cubic-bezier(0.16,1,0.3,1);
            }
            @media (prefers-reduced-motion: reduce) {
              .floe-wa-rail, .floe-wa-anchored-panel { animation: none !important; }
              .floe-wa-demo-stage { transition: none !important; }
            }
          `}</style>

          {/* Sidebar mode: a full-height right rail, slid in — and back out — on
              the same 0.3s ease-out curve as the host-page squeeze. Deliberately
              NO backdrop — the page stays interactive beside the agent (the
              Qualified model). While `railClosing` the element is already
              dismissed to clicks and only remains long enough to finish its
              slide. It stays in the accessibility tree for that brief window
              so its still-focused control is never placed under aria-hidden. */}
          {minimizedDockVisible ? (
            <section
              ref={compactDockRef}
              className="floe-wa-anchored-panel floe-wa-compact-panel"
              role="complementary"
              aria-label={getString('wa.activeCallControls')}
              style={{
                ...launcherAnchor(tier, launcherPosition, launcherInset),
                boxSizing: 'border-box',
                width: `min(${avatarDockActive ? avatarDockWidth(tier) : FLOATING_PANEL_WIDTH_PX}px, calc(100vw - ${2 * resolveLauncherInset(tier, launcherInset)}px))`,
                overflow: 'visible',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                boxShadow: 'none',
                // The pop fades the whole dock in from opacity 0 and slides it
                // up 14px — presenter included. With an approach playing the
                // presenter is ALREADY on screen (the tile, a frame ago) and is
                // being carried across by its own FLIP, so a fade here is a
                // blink in the middle of the walk-up.
                animation: avatarApproach ? 'none' : 'floe-wa-anchor-pop 0.28s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <div style={{ width: '100%', minHeight: 0 }}>{chatSurface}</div>
            </section>
          ) : railMode ? (
            <aside
              className="floe-wa-rail"
              role="complementary"
              aria-label={
                siteName?.trim() ? `${siteName.trim()} assistant` : 'Floe assistant'
              }
              style={{
                ...railLayout(tier, railWidth),
                pointerEvents: railClosing ? 'none' : 'auto',
                // `forwards` holds the last frame: without it the rail would pop
                // back to translateX(0) for the frame between the animation
                // ending and React unmounting it.
                animation: railClosing
                  ? `floe-wa-rail-out ${RAIL_TRANSITION} forwards`
                  : `floe-wa-rail-in ${RAIL_TRANSITION}`,
              }}
            >
              {/* The rail's TOP is the avatar's window while a demo runs —
                    the face stays with the conversation; the screencast lives
                    in the panel beside it. Stable child slots keep the chat
                    reconciled in place across demo start/end. */}
              {demoActive && hasAvatarWindow && (
                <div
                  style={{
                    height: RAIL_AVATAR_HEIGHT_PX,
                    flexShrink: 0,
                    position: 'relative',
                    background: 'transparent',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                  }}
                >
                  {renderAvatarWindow()}
                </div>
              )}
              <div style={{ flex: 1, minHeight: 0 }}>{chatSurface}</div>
            </aside>
          ) : (
            /* The floating chat stays attached to the launcher corner before,
               during, and after a demo. The independent stage sits beneath it. */
            <section
              className="floe-wa-anchored-panel"
              role={tier === 'fullscreen' ? undefined : 'complementary'}
              aria-label={
                tier === 'fullscreen'
                  ? undefined
                  : siteName?.trim()
                    ? `${siteName.trim()} assistant`
                    : 'Floe assistant'
              }
              style={{
                ...floatingPanelLayout(tier, launcherPosition, launcherInset),
                borderRadius: tier === 'fullscreen' ? 0 : 20,
                boxShadow:
                  tier === 'fullscreen'
                    ? undefined
                    : '0 30px 80px -24px rgba(20,18,16,0.46), 0 6px 20px rgba(20,18,16,0.14)',
                animation: 'floe-wa-anchor-pop 0.32s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <div style={{ width: '100%', minHeight: 0 }}>{chatSurface}</div>
            </section>
          )}
        </>
      )}
    </>
  );
};

/**
 * One font stack for every closed-state surface.
 *
 * FLO-531: this overrides the shadow-root base stack, so the Devanagari face
 * has to be repeated here or Hindi nudge chips / launcher copy fall back to
 * whatever the OS picks.
 */
const LAUNCHER_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans Devanagari", sans-serif';

/**
 * Shared keyframes + hover rules for the closed state (FLO-520).
 *
 * Rendered ONCE as a sibling of the chip row and the launcher, because those
 * are now two INDEPENDENT fixed elements (the chips used to be absolutely
 * positioned children of the launcher's container, fanned around it) — neither
 * can own the stylesheet any more.
 *
 * `prefers-reduced-motion` kills every entrance and hover move here. The one
 * thing CSS cannot express is "don't autoplay the video", which is why the tile
 * also takes the preference as a prop.
 */
const LauncherStyles: React.FC = () => (
  <style>{`
    @keyframes floe-wa-launcher-in {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes floe-wa-chip-in {
      from { opacity: 0; transform: translateY(8px) scale(0.94); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes floe-wa-attention-ring {
      0%, 62%, 100% { outline: 0 solid transparent; outline-offset: 0; }
      74% { outline: 2px solid var(--floe-wa-accent); outline-offset: 3px; }
      88% { outline: 1px solid transparent; outline-offset: 9px; }
    }
    @keyframes floe-wa-nudge-glow {
      0%, 66%, 100% { border-color: rgba(0,0,0,0.06); }
      78% { border-color: var(--floe-wa-accent); box-shadow: 0 12px 34px -13px var(--floe-wa-accent), 0 2px 8px rgba(0,0,0,0.1); }
    }
    @keyframes floe-wa-avatar-cta-breathe {
      0%, 58%, 100% { box-shadow: 0 10px 28px -12px rgba(0,0,0,0.46), 0 0 0 1.5px color-mix(in srgb, var(--floe-wa-accent) 45%, transparent), 0 0 22px color-mix(in srgb, var(--floe-wa-accent) 34%, transparent), 0 0 0 0 transparent; }
      72% { box-shadow: 0 12px 32px -12px rgba(0,0,0,0.5), 0 0 0 1px color-mix(in srgb, var(--floe-wa-accent) 45%, transparent), 0 0 26px color-mix(in srgb, var(--floe-wa-accent) 36%, transparent), 0 0 0 3px color-mix(in srgb, var(--floe-wa-accent) 28%, transparent); }
      86% { box-shadow: 0 10px 28px -12px rgba(0,0,0,0.46), 0 0 0 1.5px color-mix(in srgb, var(--floe-wa-accent) 45%, transparent), 0 0 22px color-mix(in srgb, var(--floe-wa-accent) 34%, transparent), 0 0 0 8px transparent; }
    }
    .floe-wa-launcher {
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      box-shadow: 0 12px 40px -8px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.10);
      animation: floe-wa-launcher-in 0.4s cubic-bezier(0.16,1,0.3,1), floe-wa-attention-ring 3.8s ease-in-out 1.1s infinite;
    }
    .floe-wa-pill-shell {
      position: relative;
      display: inline-flex;
      interpolate-size: allow-keywords;
      width: fit-content;
      min-width: 0;
      height: 68px;
      max-width: var(--floe-wa-pill-expanded-width);
      transform: scale(1);
      transition: width 0.28s cubic-bezier(0.16,1,0.3,1), transform 0.28s cubic-bezier(0.16,1,0.3,1);
    }
    .floe-wa-pill-shell[data-expanded="true"] {
      width: var(--floe-wa-pill-expanded-width);
    }
    .floe-wa-pill-shell[data-scrolling="true"] {
      transform: scale(0.82);
    }
    .floe-wa-pill-shell[data-expanded="true"] {
      transform: scale(1);
    }
    .floe-wa-pill-voice {
      position: relative;
      width: auto;
      max-width: var(--floe-wa-pill-expanded-width);
      height: 100%;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 8px 13px 8px 9px;
      overflow: hidden;
      border: 1px solid rgba(0,0,0,0.07);
      border-radius: 999px;
      background: #FFFFFF;
      color: #1A1816;
      cursor: pointer;
      font-family: ${LAUNCHER_FONT};
      text-align: left;
      transition: padding-right 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    }
    .floe-wa-pill-shell[data-expanded="true"] .floe-wa-pill-voice {
      width: 100%;
      justify-content: flex-start;
      gap: 11px;
      padding: 8px 154px 8px 9px;
    }
    .floe-wa-pill-copy {
      min-width: 0;
      flex: 1;
      width: auto;
      max-width: 220px;
      opacity: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      overflow: hidden;
      transform: none;
      transition: opacity 0.18s ease, transform 0.24s ease;
    }
    .floe-wa-pill-shell[data-expanded="true"] .floe-wa-pill-copy {
      max-width: 170px;
    }
    .floe-wa-pill-eyebrow,
    .floe-wa-pill-prompt {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .floe-wa-pill-eyebrow { font-size: 11px; font-weight: 650; color: #6F6862; }
    .floe-wa-pill-prompt { font-size: 14px; font-weight: 620; color: #1A1816; }
    /*
     * Sits immediately inboard of the keyboard button, so it clears the same
     * gutter the button reserves — derived, not a hand-summed 55 that was
     * sized for a 40px button and stayed put when it grew to 44.
     */
    .floe-wa-talk-action {
      position: absolute;
      top: 50%;
      right: ${PILL_KEYBOARD_TOUCH_GUTTER_PX}px;
      transform: translateY(-50%) scale(0.94);
      opacity: 0;
      padding: 9px 12px;
      border-radius: 999px;
      background: var(--floe-wa-accent);
      color: #FFFFFF;
      font-size: 12px;
      font-weight: 700;
      line-height: 1;
      white-space: nowrap;
      pointer-events: none;
      transition: opacity 0.16s ease, transform 0.2s ease;
    }
    .floe-wa-pill-shell[data-expanded="true"] .floe-wa-talk-action {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
    .floe-wa-pill-keyboard {
      position: absolute;
      z-index: 2;
      top: 50%;
      right: ${PILL_KEYBOARD_RIGHT_PX}px;
      transform: translateY(-50%) scale(0.9);
      width: ${PILL_KEYBOARD_SIZE_PX}px;
      height: ${PILL_KEYBOARD_SIZE_PX}px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border-radius: 50%;
      border: 1px solid rgba(0,0,0,0.08);
      background: #FFFFFF;
      color: #504A45;
      cursor: pointer;
      box-shadow: 0 4px 14px -8px rgba(0,0,0,0.35);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.16s ease, transform 0.2s ease, background 0.16s ease;
    }
    .floe-wa-pill-shell[data-expanded="true"] .floe-wa-pill-keyboard {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transform: translateY(-50%) scale(1);
    }
    .floe-wa-launcher:hover {
      transform: translateY(-2px);
      box-shadow: 0 16px 44px -8px rgba(0,0,0,0.32), 0 3px 10px rgba(0,0,0,0.12);
    }
    .floe-wa-pill-shell .floe-wa-launcher:hover { transform: none; }
    .floe-wa-tile {
      box-shadow: 0 24px 60px -18px rgba(20,18,16,0.48), 0 4px 14px rgba(20,18,16,0.16);
    }
    .floe-wa-tile:hover {
      box-shadow: 0 30px 72px -18px rgba(20,18,16,0.54), 0 6px 18px rgba(20,18,16,0.18);
    }
    .floe-wa-avatar-shell {
      isolation: isolate;
    }
    .floe-wa-avatar-shell::before {
      content: "";
      position: absolute;
      z-index: 0;
      left: 8%;
      right: 8%;
      bottom: 3%;
      height: 62%;
      border-radius: 50%;
      background: radial-gradient(ellipse at 50% 72%, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.08) 38%, rgba(0,0,0,0) 72%);
      background: radial-gradient(ellipse at 50% 72%, color-mix(in srgb, var(--floe-wa-accent) 18%, rgba(0,0,0,0.16)) 0%, color-mix(in srgb, var(--floe-wa-accent) 8%, transparent) 40%, transparent 72%);
      filter: blur(12px);
      opacity: 0.72;
      pointer-events: none;
    }
    .floe-wa-avatar-voice,
    .floe-wa-avatar-voice:hover {
      position: relative;
      z-index: 1;
      box-shadow: none;
      animation: floe-wa-launcher-in 0.4s cubic-bezier(0.16,1,0.3,1);
      transform: none;
    }
    .floe-wa-avatar-voice-cta {
      animation: floe-wa-avatar-cta-breathe 3.8s ease-in-out 1s infinite;
    }
    .floe-wa-avatar-keyboard {
      position: absolute;
      z-index: 3;
      right: -4px;
      bottom: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border-radius: 50%;
      border: 1px solid rgba(0,0,0,0.08);
      background: #FFFFFF;
      color: #504A45;
      cursor: pointer;
      box-shadow: 0 8px 22px -10px rgba(0,0,0,0.4);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transform: translateY(4px) scale(0.9);
      transition: opacity 0.16s ease, transform 0.2s ease, background 0.16s ease;
    }
    .floe-wa-avatar-shell:hover .floe-wa-avatar-keyboard,
    .floe-wa-avatar-shell:focus-within .floe-wa-avatar-keyboard {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transform: none;
    }
    .floe-wa-nudge {
      transition: transform 0.14s ease, box-shadow 0.16s ease, border-color 0.14s ease;
    }
    .floe-wa-nudge:hover {
      transform: translateY(-1px);
      border-color: rgba(0,0,0,0.14);
      box-shadow: 0 12px 30px -10px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.1);
    }
    .floe-wa-post-call-booking {
      transition: transform 0.14s ease, filter 0.16s ease, box-shadow 0.16s ease;
    }
    .floe-wa-post-call-booking:hover { transform: translateY(-1px); filter: brightness(0.97); }
    .floe-wa-launcher:focus-visible {
      animation-play-state: running, paused;
      outline: 3px solid var(--floe-wa-accent) !important;
      outline-offset: 3px !important;
    }
    .floe-wa-keyboard:focus-visible, .floe-wa-nudge:focus-visible, .floe-wa-post-call-booking:focus-visible {
      animation-play-state: paused;
    }
    .floe-wa-launcher:focus-visible, .floe-wa-keyboard:focus-visible, .floe-wa-nudge:focus-visible, .floe-wa-post-call-booking:focus-visible {
      outline: 3px solid var(--floe-wa-accent);
      outline-offset: 3px;
    }
    .floe-wa-keyboard {
      transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
    }
    .floe-wa-keyboard:hover {
      transform: translateY(-1px);
      background: #FFFFFF;
      box-shadow: 0 10px 28px -12px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.08);
    }
    @media (prefers-reduced-motion: reduce) {
      .floe-wa-launcher, .floe-wa-chiprow, .floe-wa-chip { animation: none !important; }
      .floe-wa-launcher, .floe-wa-keyboard, .floe-wa-nudge, .floe-wa-post-call-booking, .floe-wa-pill-shell, .floe-wa-pill-copy, .floe-wa-talk-action, .floe-wa-avatar-voice-cta { transition: none !important; animation: none !important; }
      .floe-wa-pill-shell[data-scrolling="true"] { transform: none; }
      .floe-wa-launcher:hover, .floe-wa-keyboard:hover, .floe-wa-nudge:hover, .floe-wa-post-call-booking:hover { transform: none; }
    }
    /*
     * hover:none alone tests the PRIMARY pointer: a touch laptop, or an iPad
     * with a trackpad attached, reports hover:hover, and a finger tap there
     * still starts voice before the keyboard button is tappable. Hence
     * any-pointer:coarse too — it catches the finger whether or not it is the
     * primary pointer.
     */
    @media (hover: none), (any-pointer: coarse) {
      .floe-wa-avatar-keyboard {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: none;
        /*
         * Revealing it permanently on the DESKTOP tile puts it in the CTA's
         * band at the resting bottom: 4px — the same collision the compact
         * tile had, now reachable on a touch laptop or a trackpad iPad at
         * >=1024px. The compact tiers lift via the inline style, which is more
         * specific than this and still wins there; the desktop tier lifts here.
         */
        bottom: ${LAUNCHER_TILE_KEYBOARD_BOTTOM_TOUCH_PX}px;
      }
      /*
       * The PILL's keyboard action was reachable only through
       * [data-expanded="true"], i.e. pointerActive || keyboardFocusActive. On
       * touch the very gesture that sets pointerActive also fires the voice
       * button's onClick, so the session started before the button could be
       * tapped — and on a site with no video avatar (the pill IS the launcher
       * there) that left NO way into text entry from the resting state.
       * Reveal it permanently, exactly as the tile's button already is.
       */
      .floe-wa-pill-keyboard {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transform: translateY(-50%) scale(1);
      }
      /*
       * …and give it room in the COLLAPSED pill, which reserves a gutter only
       * while expanded, or the prompt copy runs underneath the button. The
       * gutter is derived from the button's own offset, size and gap, never
       * summed by hand. The expanded rule is more specific (two classes + an
       * attribute), so both the hover expansion and the expanded touch pill
       * keep their 154px gutter untouched — this only widens the resting pill
       * on touch, and the shell's max-width still clamps it to the viewport.
       */
      .floe-wa-pill-voice {
        padding-right: ${PILL_KEYBOARD_TOUCH_GUTTER_PX}px;
      }
    }
  `}</style>
);

/**
 * Closed-state suggested prompts (FLO-520). A pill launcher gets a compact
 * corner stack; a live-video tile gets the same stack in the lane beside the
 * presenter, where the live bubble will later sit. Both replace the old radial
 * fan and the bottom-centre row that floated the prompts away from the face.
 *
 * The caller has already capped the list to what fits ({@link chipRowCapacity}),
 * so this renders exactly what it is given. The optional booking action is a
 * post-call outcome and deliberately bypasses nudge telemetry.
 */
const WebsiteAgentChipRow: React.FC<{
  accent: string;
  tier: ViewportTier;
  variant: LauncherVariant;
  position?: LauncherPosition;
  launcherInset?: number;
  siteName?: string;
  nudges: WebsiteAgentNudge[];
  /** Set while the resting presenter stands in a host anchor instead of the corner. */
  anchor?: PresenterAnchorState | null;
  onNudge: (nudge: WebsiteAgentNudge) => void;
  bookingAction?: {
    label: string;
    onClick: () => void;
    onInteractionChange: (interacting: boolean) => void;
    pending?: boolean;
  };
}> = ({
  accent,
  tier,
  variant,
  position,
  launcherInset,
  siteName,
  nudges,
  anchor,
  onNudge,
  bookingAction,
}) => {
  const [bookingHovered, setBookingHovered] = useState(false);
  const [bookingFocused, setBookingFocused] = useState(false);
  if (nudges.length === 0 && !bookingAction) return null;
  const groupLabel = bookingAction
    ? getString('wa.bookCall')
    : siteName?.trim()
      ? `Suggested questions for ${siteName.trim()}`
      : 'Suggested questions';
  return (
    <div
      // Remount on a corner ⇄ anchor move so the prompts re-enter beside the
      // presenter's new place instead of jumping while it travels.
      key={anchor?.box ? 'anchored' : 'corner'}
      className="floe-wa-chiprow"
      // The visual cue that these are the agent's prompts is the accent spark,
      // which is decorative — so the same information is carried by a labelled
      // group for anyone who never sees it.
      role="group"
      aria-label={groupLabel}
      style={{
        ...(anchor?.box
          ? anchoredChipRowLayout(anchor.box, anchor.viewport)
          : chipRowLayout(tier, launcherInset, variant, position)),
        fontFamily: LAUNCHER_FONT,
        animation: 'floe-wa-launcher-in 0.4s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {nudges.map((n, i) => (
        <button
          key={n.id}
          type="button"
          onClick={() => onNudge(n)}
          className="floe-wa-nudge floe-wa-chip"
          style={{
            ...({ '--floe-wa-accent': accent } as React.CSSProperties),
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            // Both variants stack, so a chip is content-sized up to the lane
            // and long operator copy wraps to two lines, then ellipsizes.
            // box-sizing is pinned because the overlay bundles no global reset.
            boxSizing: 'border-box',
            flex: '0 1 auto',
            width: variant === 'pill' ? '100%' : undefined,
            minWidth: 0,
            maxWidth: variant === 'pill' ? 340 : '100%',
            padding: variant === 'pill' ? '12px 18px' : '11px 16px',
            background: '#FFFFFF',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 16,
            cursor: 'pointer',
            fontSize: variant === 'pill' ? 14 : 13.5,
            fontWeight: 500,
            color: '#1C1917',
            textAlign: 'left',
            lineHeight: 1.35,
            whiteSpace: 'normal',
            boxShadow: '0 10px 30px -12px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.08)',
            animation: `floe-wa-chip-in 0.36s cubic-bezier(0.16,1,0.3,1) ${0.05 * i}s both, floe-wa-nudge-glow 4.6s ease-in-out ${1.4 + i * 0.45}s infinite`,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.copy}</span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        </button>
      ))}

      {bookingAction && (
        <button
          type="button"
          onClick={bookingAction.onClick}
          disabled={bookingAction.pending}
          aria-busy={bookingAction.pending || undefined}
          onMouseEnter={() => {
            setBookingHovered(true);
            bookingAction.onInteractionChange(true);
          }}
          onMouseLeave={() => {
            setBookingHovered(false);
            bookingAction.onInteractionChange(bookingFocused);
          }}
          onFocus={() => {
            setBookingFocused(true);
            bookingAction.onInteractionChange(true);
          }}
          onBlur={() => {
            setBookingFocused(false);
            bookingAction.onInteractionChange(bookingHovered);
          }}
          className="floe-wa-post-call-booking floe-wa-chip"
          style={{
            ...({ '--floe-wa-accent': accent } as React.CSSProperties),
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            flex: '0 1 auto',
            width: variant === 'pill' ? '100%' : undefined,
            minWidth: 0,
            maxWidth: variant === 'pill' ? 340 : 260,
            padding: variant === 'pill' ? '12px 18px' : '10px 18px',
            border: '1px solid transparent',
            borderRadius: 16,
            background: accent,
            color: '#FFFFFF',
            cursor: bookingAction.pending ? 'wait' : 'pointer',
            opacity: bookingAction.pending ? 0.78 : 1,
            fontFamily: LAUNCHER_FONT,
            fontSize: variant === 'pill' ? 14 : 13.5,
            fontWeight: 700,
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            boxShadow: `0 12px 30px -12px ${accent}, 0 2px 8px rgba(0,0,0,0.12)`,
            animation: `floe-wa-chip-in 0.36s cubic-bezier(0.16,1,0.3,1) ${0.05 * nudges.length}s both`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M16 3v4M8 3v4M3 10h18" />
          </svg>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {bookingAction.pending ? getString('wa.status.connecting') : bookingAction.label}
          </span>
        </button>
      )}
    </div>
  );
};

/**
 * The corner launcher (FLO-520). Two variants, one anchor:
 *   'tile' — Anam-style portrait video card playing the site's idle loop.
 *   'pill' — the compact "Ask {agent}" pill (what every site sees today,
 *            re-anchored from bottom-center to the configured corner).
 * After the Video avatar gate, the variant is derived from its media
 * ({@link launcherVariant}); there is no launcher-specific override. Both spread
 * {@link launcherAnchor}, so a mid-session
 * degrade (dead video URL) swaps the card without moving the corner.
 */
const WebsiteAgentLauncher: React.FC<{
  variant: LauncherVariant;
  /** Host anchor tracking (tile only); null keeps the classic corner anchor. */
  anchor?: PresenterAnchorState | null;
  tier: ViewportTier;
  position?: LauncherPosition;
  launcherInset?: number;
  media: AvatarMedia;
  intro?: AvatarIntroMedia;
  playRetreat: boolean;
  retreatFromBox: PresenterBox | null;
  registerPresenterEl: (el: HTMLElement | null) => void;
  onRetreatConsumed: () => void;
  isMediaReady: (url: string) => boolean;
  onIntroMediaFailure: () => void;
  reducedMotion: boolean;
  onMediaFailure: () => void;
  siteName?: string;
  siteLogoUrl?: string;
  siteFaviconUrl?: string;
  accent: string;
  prompt?: string;
  scrolling: boolean;
  expanded: boolean;
  onPointerInteractionChange: (active: boolean) => void;
  onKeyboardFocusChange: (active: boolean) => void;
  onOpen: () => void;
  onUseKeyboard: () => void;
}> = ({
  variant,
  anchor,
  tier,
  position,
  launcherInset,
  media,
  intro,
  playRetreat,
  retreatFromBox,
  registerPresenterEl,
  onRetreatConsumed,
  isMediaReady: isReady,
  onIntroMediaFailure,
  reducedMotion,
  onMediaFailure,
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accent,
  prompt,
  scrolling,
  expanded,
  onPointerInteractionChange,
  onKeyboardFocusChange,
  onOpen,
  onUseKeyboard,
}) => {
  // While anchor tracking is on, the tile is placed by an explicit top/left box
  // in BOTH places (the anchor, or the corner expressed as top/left), so moving
  // between them is a transition rather than a jump.
  const placed =
    variant === 'tile' && anchor && anchor.viewport.width > 0
      ? (anchor.box ?? cornerPresenterBox(tier, anchor.viewport, position, launcherInset))
      : null;
  // Only the corner ⇄ anchor switch animates; an anchor moving under a presenter
  // already standing in it is tracked instantly (see PresenterAnchorState.handoff).
  const handoff =
    reducedMotion || !anchor?.handoff
      ? undefined
      : ['top', 'left', 'width', 'height']
        .map((prop) => `${prop} ${PRESENTER_HANDOFF_MS}ms cubic-bezier(0.2, 0.7, 0.2, 1)`)
        .join(', ');
  return (
    <div
      style={{
        ...(placed
          ? {
              left: placed.left,
              pointerEvents: 'auto' as const,
              position: 'fixed' as const,
              top: placed.top,
              transition: handoff,
              zIndex: LAUNCHER_TILE_Z,
            }
          : launcherAnchor(tier, position, launcherInset)),
        ...({ '--floe-wa-accent': accent } as React.CSSProperties),
        fontFamily: LAUNCHER_FONT,
      }}
    >
      {variant === 'tile' ? (
        <AvatarLauncherTile
          size={placed ? { height: placed.height, width: placed.width } : undefined}
          sizeTransition={placed ? handoff : undefined}
          tier={tier}
          media={media}
          intro={intro}
          playRetreat={playRetreat}
          retreatFromBox={retreatFromBox}
          registerPresenterEl={registerPresenterEl}
          onRetreatConsumed={onRetreatConsumed}
          isMediaReady={isReady}
          onIntroMediaFailure={onIntroMediaFailure}
          reducedMotion={reducedMotion}
          onMediaFailure={onMediaFailure}
          accent={accent}
          onOpen={onOpen}
          onUseKeyboard={onUseKeyboard}
        />
      ) : (
        <AskAgentPill
          tier={tier}
          position={position}
          launcherInset={launcherInset}
          siteName={siteName}
          siteLogoUrl={siteLogoUrl}
          siteFaviconUrl={siteFaviconUrl}
          accent={accent}
          prompt={prompt}
          scrolling={scrolling}
          expanded={expanded}
          onPointerInteractionChange={onPointerInteractionChange}
          onKeyboardFocusChange={onKeyboardFocusChange}
          onOpen={onOpen}
          onUseKeyboard={onUseKeyboard}
        />
      )}
    </div>
  );
};

/**
 * Variant A — a transparent presenter with a stable voice CTA. Contextual copy
 * stays in WebsiteAgentChipRow; it never replaces the launcher's job. The
 * keyboard action is a sibling (never a nested button) and appears only on
 * hover/focus so the default commitment remains one clear voice-first action.
 */
const AvatarLauncherTile: React.FC<{
  /** An anchored presenter's size; defaults to the tier's corner tile. */
  size?: { width: number; height: number };
  sizeTransition?: string;
  tier: ViewportTier;
  media: AvatarMedia;
  /** The usable approach intro; undefined → rest on the close loop, as before. */
  intro?: AvatarIntroMedia;
  playRetreat: boolean;
  /** The docked presenter's box at the moment the call ended, for the walk back. */
  retreatFromBox: PresenterBox | null;
  registerPresenterEl: (el: HTMLElement | null) => void;
  onRetreatConsumed: () => void;
  isMediaReady: (url: string) => boolean;
  onIntroMediaFailure: () => void;
  reducedMotion: boolean;
  onMediaFailure: () => void;
  accent: string;
  onOpen: () => void;
  onUseKeyboard: () => void;
}> = ({
  size: sizeOverride,
  sizeTransition,
  tier,
  media,
  intro,
  playRetreat,
  retreatFromBox,
  registerPresenterEl,
  onRetreatConsumed,
  isMediaReady: isReady,
  onIntroMediaFailure,
  reducedMotion,
  onMediaFailure: onCloseMediaFailure,
  accent,
  onOpen,
  onUseKeyboard,
}) => {
  const size = sizeOverride ?? launcherTileSize(tier);
  const compact = tier !== 'squeeze';
  // With an intro the tile rests on the FAR idle loop (plus the odd gesture,
  // and the retreat when it has just come back from a call); without one it
  // plays the close loop exactly as it always has.
  const rest = useAvatarRestSequence({
    intro,
    closePosterUrl: media.posterUrl,
    active: !reducedMotion,
    playRetreat,
    onRetreatConsumed,
    isReady,
  });
  const restSrc = rest ? rest.src : media.idleLoopUrl;
  // The walk back starts at the DOCK's size and place and settles into the
  // tile's — the mirror of the approach. Only when the retreat actually plays:
  // shrinking a resting presenter for no visible reason would be worse than a cut.
  // Latched at mount: whether this tile OPENED with the walk back.
  // Both halves: with no measurable box to walk back FROM (the call ended from
  // the expanded panel, which has no docked presenter) there is no FLIP, and
  // suppressing the launcher's entrance as well would leave the tile popping in
  // with no animation at all.
  const openedWithRetreat = useRef(rest?.clip.kind === 'retreat' && !!retreatFromBox).current;
  const flipRef = usePresenterFlip(retreatFromBox, openedWithRetreat);
  const presenterBoxRef = useCallback(
    (el: HTMLDivElement | null) => {
      flipRef.current = el;
      registerPresenterEl(el);
    },
    [flipRef, registerPresenterEl],
  );
  // A far clip that fails retires the INTRO; only the close loop failing
  // degrades the launcher to the pill.
  const onMediaFailure = rest ? onIntroMediaFailure : onCloseMediaFailure;
  // Did the media prove it can play? Cleared/rearmed per URL by the effect.
  const settledRef = useRef(false);

  // `onError` catches a hard failure (404, decode error) immediately; this
  // catches the URL that simply never answers (dead CDN, blocked host, offline)
  // — otherwise the corner keeps a black rectangle for the whole pageview.
  useEffect(() => {
    // The reduced-motion branch paints an <img>, which has its own onError.
    if (reducedMotion || !restSrc) return;
    settledRef.current = false;
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      timer = setTimeout(() => {
        if (settledRef.current) return;
        // A backgrounded tab throttles media loading — that is not a broken URL,
        // and degrading here would flip the launcher behind the visitor's back.
        // RE-ARM rather than stand down: disarming would leave a tab that
        // happened to be hidden at t=5s showing a black rectangle for the rest
        // of the pageview, which is the exact outcome this timer exists to
        // prevent. (Hidden tabs throttle timers to ~1/min, so this costs
        // nothing while away and fires promptly once the visitor is back.)
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          arm();
          return;
        }
        onMediaFailure();
      }, LAUNCHER_VIDEO_TIMEOUT_MS);
    };
    arm();
    return () => clearTimeout(timer);
    // Keyed on whether there IS a rest clip, not on which one: a gesture taking
    // over from the far loop is not a fresh load to be timed out.
  }, [!!restSrc, !!rest, reducedMotion, onMediaFailure]);

  const settle = useCallback(() => {
    settledRef.current = true;
  }, []);

  return (
    <div
      className="floe-wa-avatar-shell"
      style={{
        ...({ '--floe-wa-accent': accent } as React.CSSProperties),
        width: size.width,
        height: size.height,
        position: 'relative',
        fontFamily: LAUNCHER_FONT,
        transition: sizeTransition,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-floe-wa-primary-launcher="true"
        aria-label={getString('wa.talkToMe')}
        className="floe-wa-launcher floe-wa-tile floe-wa-avatar-voice"
        style={{
          width: '100%',
          height: '100%',
          padding: 0,
          border: 'none',
          overflow: 'visible',
          background: 'transparent',
          cursor: 'pointer',
          display: 'block',
          fontFamily: LAUNCHER_FONT,
          // The launcher's entrance fades in from opacity 0 and slides up 12px.
          // Coming back from a call the presenter is already on screen and is
          // walking back under its own FLIP: fading it in would blink it out
          // first, and the slide would throw the FLIP's measurement off.
          ...(openedWithRetreat ? { animation: 'none' } : {}),
        }}
      >
        {/* Its own box, so the intro FLIP moves the presenter and not the CTA
            pill that sits on top of it. */}
        <div ref={presenterBoxRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <AvatarVideo
            src={restSrc}
            poster={rest ? rest.poster : media.posterUrl}
            loop={rest ? rest.loop : true}
            onEnded={rest?.onEnded}
            // A dead far clip must retire the intro even though its poster
            // loaded — `onError` only fires once both have failed.
            onVideoError={rest ? onIntroMediaFailure : undefined}
            reducedMotion={reducedMotion}
            objectFit="contain"
            onReady={settle}
            onError={onMediaFailure}
            edgeBlur={AVATAR_EDGE_BLUR}
            style={{ pointerEvents: 'none', ...avatarEdgeFade() }}
          />
        </div>
        <span
          className="floe-wa-avatar-voice-cta"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: compact ? LAUNCHER_TILE_CTA_BOTTOM_COMPACT_PX : LAUNCHER_TILE_CTA_BOTTOM_PX,
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 5 : 7,
            boxSizing: 'border-box',
            maxWidth: `calc(100% - ${compact ? 8 : 16}px)`,
            padding: compact ? '7px 10px' : '10px 15px',
            background: '#FFFFFF',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 999,
            fontSize: compact ? 10.5 : 13,
            fontWeight: 700,
            color: '#1A1816',
            whiteSpace: 'nowrap',
            boxShadow: '0 10px 28px -12px rgba(0,0,0,0.46)',
            pointerEvents: 'none',
          }}
        >
          <svg width={compact ? 12 : 15} height={compact ? 12 : 15} viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8" />
          </svg>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {getString('wa.talkToMe')}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onUseKeyboard}
        aria-label={getString('wa.useKeyboard')}
        title={getString('wa.useKeyboard')}
        className="floe-wa-keyboard floe-wa-avatar-keyboard"
        // Driven by the SAME `tier` that sizes the tile, so the offset can
        // never disagree with launcherTileSize() the way a hardcoded width
        // media query would. Desktop passes nothing and keeps the
        // stylesheet's bottom: 4px, pixel for pixel.
        style={{
          width: LAUNCHER_TILE_KEYBOARD_SIZE_PX,
          height: LAUNCHER_TILE_KEYBOARD_SIZE_PX,
          ...(compact ? { bottom: LAUNCHER_TILE_KEYBOARD_BOTTOM_COMPACT_PX } : null),
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M7 9h.01M11 9h.01M15 9h.01M18 9h.01M7 13h.01M11 13h.01M15 13h.01M8 16h8" />
        </svg>
      </button>
    </div>
  );
};

/**
 * Variant B — the compact "Ask {agent}" pill. Byte-for-byte the launcher every
 * site sees today, moved from the bottom-center anchor to the shared corner
 * anchor. Pointer and keyboard-visible focus are explicit state so a scroll can
 * fold away a stale reveal without sacrificing the next intentional interaction.
 */
const AskAgentPill: React.FC<{
  tier: ViewportTier;
  position?: LauncherPosition;
  launcherInset?: number;
  siteName?: string;
  siteLogoUrl?: string;
  siteFaviconUrl?: string;
  accent: string;
  prompt?: string;
  scrolling: boolean;
  expanded: boolean;
  onPointerInteractionChange: (active: boolean) => void;
  onKeyboardFocusChange: (active: boolean) => void;
  onOpen: () => void;
  onUseKeyboard: () => void;
}> = ({
  tier,
  position,
  launcherInset,
  siteName,
  siteLogoUrl,
  siteFaviconUrl,
  accent,
  prompt,
  scrolling,
  expanded,
  onPointerInteractionChange,
  onKeyboardFocusChange,
  onOpen,
  onUseKeyboard,
}) => {
  const letter = (siteName?.trim()?.[0] || 'F').toUpperCase();
  const inset = resolveLauncherInset(tier, launcherInset);
  const expandedWidth = `min(${LAUNCHER_MAX_WIDTH_PX}px, calc(100vw - ${2 * inset}px))`;

  return (
    <div
      className="floe-wa-pill-shell"
      data-scrolling={scrolling ? 'true' : 'false'}
      data-expanded={expanded ? 'true' : 'false'}
      onPointerEnter={() => onPointerInteractionChange(true)}
      onPointerMove={() => onPointerInteractionChange(true)}
      onPointerLeave={() => onPointerInteractionChange(false)}
      onPointerCancel={() => onPointerInteractionChange(false)}
      onFocusCapture={(event) => {
        onKeyboardFocusChange(
          event.target instanceof HTMLElement && event.target.matches(':focus-visible'),
        );
      }}
      onKeyDownCapture={() => onKeyboardFocusChange(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          onKeyboardFocusChange(false);
        }
      }}
      style={{
        ...({
          '--floe-wa-accent': accent,
          '--floe-wa-pill-expanded-width': expandedWidth,
          transformOrigin: position === 'bottom-left' ? 'bottom left' : 'bottom right',
        } as React.CSSProperties),
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        data-floe-wa-primary-launcher="true"
        aria-label={prompt
          ? `${getString('wa.ask', { name: siteName || 'Floe' })}: ${prompt}`
          : siteName
            ? getString('wa.askAnything', { name: siteName })
            : getString('wa.askQuestion')}
        className="floe-wa-launcher floe-wa-pill-voice"
      >
        <LauncherMark
          siteLogoUrl={siteLogoUrl}
          siteFaviconUrl={siteFaviconUrl}
          letter={letter}
          accent={accent}
          size={40}
        />
        <span className="floe-wa-pill-copy">
          <span className="floe-wa-pill-eyebrow">
            {getString('wa.ask', { name: siteName || 'Floe' })}
          </span>
          <span className="floe-wa-pill-prompt">
            {prompt || getString('wa.askAnything', { name: siteName || 'Floe' })}
          </span>
        </span>
        <span className="floe-wa-talk-action" aria-hidden="true">
          {getString('wa.talkToAgent')}
        </span>
      </button>
      <button
        type="button"
        onClick={onUseKeyboard}
        aria-label={getString('wa.useKeyboard')}
        title={getString('wa.useKeyboard')}
        className="floe-wa-keyboard floe-wa-pill-keyboard"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M7 9h.01M11 9h.01M15 9h.01M18 9h.01M7 13h.01M11 13h.01M15 13h.01M8 16h8" />
        </svg>
      </button>
    </div>
  );
};
